<template>
    <div class="page">
        <div class="page-header">
            <h1>會員詳情</h1>
            <p>查看會員的詳細資料與紀錄</p>
        </div>

        <div class="content-grid">
            <!-- 左邊：功能選單 -->
            <div class="menu-panel">
                <div class="panel-header">
                    <h4>功能選單</h4>
                </div>

                <div class="menu-list">
                    <div
                        v-for="item in menuItems"
                        :key="item.key"
                        class="menu-item"
                        :class="{ active: activeTab === item.key }"
                        @click="activeTab = item.key"
                    >
                        <span class="menu-text">{{ item.label }}</span>
                    </div>

                    <div v-if="menuItems.length === 0" class="no-permission">
                        <p>您沒有任何查看權限</p>
                    </div>
                </div>
            </div>

            <!-- 右邊：內容顯示區 -->
            <div class="content-panel">
                <div class="panel-header">
                    <h4>{{ currentMenuItem?.label || '' }}</h4>
                    <div
                        v-if="activeTab === 'detail' && canViewDetail"
                        class="header-actions"
                    >
                        <a-button
                            v-if="!isEditing && canDeregisterPartner"
                            danger
                            @click="openDeregisterModal"
                        >
                            除籍
                        </a-button>
                        <a-button
                            v-if="!isEditing && canToggleBlacklist && isAccountBlacklisted"
                            @click="openUnblacklistModal"
                        >
                            解除黑名單
                        </a-button>
                        <a-button
                            v-if="!isEditing && canToggleBlacklist && !isAccountBlacklisted"
                            danger
                            ghost
                            @click="openBlacklistModal"
                        >
                            列入黑名單
                        </a-button>
                        <a-button
                            v-if="!isEditing && (canEdit || canEditReferralOnly)"
                            type="primary"
                            class="vaporwave-button"
                            @click="enterEditMode"
                        >
                            編輯
                        </a-button>
                        <template v-else-if="isEditing && (canEdit || canEditReferralOnly)">
                            <a-button @click="cancelEdit">取消</a-button>
                            <a-button
                                type="primary"
                                class="vaporwave-button"
                                @click="saveMemberDetail"
                                :loading="saving"
                            >
                                儲存
                            </a-button>
                        </template>
                    </div>
                </div>

                <div v-if="loading" class="loading-state">
                    <a-spin size="large" />
                    <p>載入中...</p>
                </div>

                <div v-else class="content-area">
                    <!-- 基本資料 -->
                    <div
                        v-if="activeTab === 'detail' && memberDetail"
                        class="detail-content"
                    >
                        <!-- 個人資訊區塊 -->
                        <div class="info-section">
                            <div class="section-title">個人資訊</div>
                            <table class="info-table">
                                <tbody>
                                    <tr>
                                        <td class="info-label">姓名</td>
                                        <td class="info-value">
                                            <a-input
                                                v-if="isEditing"
                                                v-model:value="editForm.name"
                                                placeholder="請輸入姓名"
                                            />
                                            <span v-else>
                                                {{ memberDetail.name }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">Email</td>
                                        <td class="info-value">
                                            <a-input
                                                v-if="isEditing"
                                                v-model:value="editForm.email"
                                                placeholder="請輸入 Email"
                                            />
                                            <span v-else class="verified-row">
                                                <span>
                                                    {{ memberDetail.email }}
                                                </span>
                                                <span
                                                    class="verified-icon"
                                                    v-if="
                                                        memberDetail.email_verified_at
                                                    "
                                                >
                                                    <CheckCircleOutlined
                                                        class="icon-verified"
                                                    />
                                                </span>
                                                <span
                                                    class="verified-icon"
                                                    v-else
                                                >
                                                    <CloseCircleOutlined
                                                        class="icon-unverified"
                                                    />
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">手機號碼</td>
                                        <td class="info-value">
                                            <a-input
                                                v-if="isEditing"
                                                v-model:value="editForm.phone"
                                                placeholder="請輸入手機號碼"
                                            />
                                            <span v-else class="verified-row">
                                                <span>
                                                    {{
                                                        memberDetail.phone ||
                                                        '-'
                                                    }}
                                                </span>
                                                <span
                                                    class="verified-icon"
                                                    v-if="
                                                        memberDetail.phone_verified_at
                                                    "
                                                >
                                                    <CheckCircleOutlined
                                                        class="icon-verified"
                                                    />
                                                </span>
                                                <span
                                                    class="verified-icon"
                                                    v-else
                                                >
                                                    <CloseCircleOutlined
                                                        class="icon-unverified"
                                                    />
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">性別</td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing"
                                                v-model:value="editForm.gender"
                                                placeholder="請選擇性別"
                                                style="width: 100%"
                                            >
                                                <a-select-option value="男性">
                                                    男性
                                                </a-select-option>
                                                <a-select-option value="女性">
                                                    女性
                                                </a-select-option>
                                            </a-select>
                                            <span v-else>
                                                {{
                                                    memberDetail.gender ===
                                                    'male'
                                                        ? '男性'
                                                        : memberDetail.gender ===
                                                            'female'
                                                          ? '女性'
                                                          : memberDetail.gender ||
                                                            '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="canViewSensitivePii">
                                        <td class="info-label">生日</td>
                                        <td class="info-value">
                                            <a-date-picker
                                                v-if="isEditing"
                                                v-model:value="
                                                    editForm.birthdate
                                                "
                                                placeholder="請選擇生日"
                                                style="width: 100%"
                                                format="YYYY-MM-DD"
                                                value-format="YYYY-MM-DD"
                                            />
                                            <span v-else>
                                                {{
                                                    memberDetail.birthdate ||
                                                    '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="canViewSensitivePii">
                                        <td class="info-label">
                                            身分證／居留證統一證號
                                        </td>
                                        <td class="info-value">
                                            <a-input
                                                v-if="isEditing"
                                                v-model:value="
                                                    editForm.id_number
                                                "
                                                placeholder="本國身分證或居留證統一證號"
                                            />
                                            <span v-else>
                                                {{
                                                    memberDetail.id_number ||
                                                    '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">Line ID</td>
                                        <td class="info-value">
                                            <a-input
                                                v-if="isEditing"
                                                v-model:value="editForm.line_id"
                                                placeholder="請輸入 Line ID"
                                            />
                                            <span v-else>
                                                {{
                                                    memberDetail.line_id || '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">帳號狀態</td>
                                        <td
                                            class="info-value"
                                            :class="{
                                                'status-active':
                                                    memberDetail.status,
                                                'status-inactive':
                                                    !memberDetail.status
                                            }"
                                        >
                                            <span>
                                                <a-tag
                                                    :color="
                                                        memberDetail.status
                                                            ? 'green'
                                                            : 'red'
                                                    "
                                                >
                                                    {{
                                                        memberDetail.status
                                                            ? '正常'
                                                            : '帳號黑名單'
                                                    }}
                                                </a-tag>
                                                <a-tag
                                                    v-if="
                                                        memberDetail.identity_blocked
                                                    "
                                                    color="volcano"
                                                >
                                                    識別黑名單（無法登入）
                                                </a-tag>
                                                <a-tag
                                                    v-if="memberDetail.is_deregistered"
                                                    color="orange"
                                                >
                                                    除籍
                                                </a-tag>
                                                <span
                                                    v-if="
                                                        memberDetail
                                                            .access_blocklist_hit
                                                            ?.reason
                                                    "
                                                    class="identity-block-reason"
                                                >
                                                    原因：{{
                                                        memberDetail
                                                            .access_blocklist_hit
                                                            .reason
                                                    }}
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">推薦碼</td>
                                        <td class="info-value">
                                            <span>
                                                {{
                                                    memberDetail.referral_code ||
                                                    '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 積分小卡片（顯示於個人資訊下方） -->
                        <div v-if="pointsArray.length" class="info-section">
                            <div class="section-title">積分</div>
                            <div class="points-grid">
                                <a-card
                                    v-for="pt in pointsArray"
                                    :key="pt.code"
                                    hoverable
                                    class="points-card"
                                >
                                    <div class="points-card-header">
                                        <span class="points-code">
                                            {{ pt.code }}
                                        </span>
                                        <a-tag class="points-total-tag">
                                            總額 {{ pt.total }}
                                        </a-tag>
                                    </div>
                                    <div class="points-metrics">
                                        <div class="metric">
                                            <span class="metric-label">
                                                可用
                                            </span>
                                            <span class="metric-value usable">
                                                {{ pt.usable }}
                                            </span>
                                        </div>
                                        <div class="metric">
                                            <span class="metric-label">
                                                凍結
                                            </span>
                                            <span class="metric-value frozen">
                                                {{ pt.frozen }}
                                            </span>
                                        </div>
                                        <div class="metric">
                                            <span class="metric-label">
                                                累計獲得
                                            </span>
                                            <span class="metric-value earned">
                                                {{ pt.cumulative_earned }}
                                            </span>
                                        </div>
                                    </div>
                                </a-card>
                            </div>
                        </div>

                        <!-- 帳號資訊區塊 -->
                        <div class="info-section">
                            <div class="section-title">帳號資訊</div>
                            <table class="info-table">
                                <tbody>
                                    <tr>
                                        <td class="info-label">身分</td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing"
                                                v-model:value="
                                                    editForm.identity
                                                "
                                                placeholder="請選擇身分"
                                                style="width: 100%"
                                            >
                                                <a-select-option value="guest">
                                                    遊客
                                                </a-select-option>
                                                <a-select-option
                                                    value="student"
                                                >
                                                    學員
                                                </a-select-option>
                                                <a-select-option
                                                    value="partner"
                                                >
                                                    夥伴
                                                </a-select-option>
                                                <a-select-option
                                                    value="manager"
                                                >
                                                    管理員
                                                </a-select-option>
                                            </a-select>
                                            <span
                                                v-else
                                                class="flex items-center gap-2"
                                            >
                                                <a-tag
                                                    :color="
                                                        getIdentityTagColor(
                                                            memberDetail.identity
                                                        )
                                                    "
                                                >
                                                    {{
                                                        getIdentityText(
                                                            memberDetail.identity
                                                        )
                                                    }}
                                                </a-tag>
                                                <a-tooltip
                                                    v-if="
                                                        canViewPartnerOrg &&
                                                        memberDetail.identity ===
                                                            'partner'
                                                    "
                                                    title="查看組織圖"
                                                >
                                                    <a-button
                                                        class="orgchart-btn"
                                                        size="small"
                                                        @click="openOrgChart"
                                                    >
                                                        <template #icon>
                                                            <ApartmentOutlined />
                                                        </template>
                                                    </a-button>
                                                </a-tooltip>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 用戶管理：只改推薦人，推薦事業部跟著帶入 -->
                        <div
                            v-if="isGeneralAdminDetail && !canViewPartnerOrg"
                            class="info-section"
                        >
                            <div class="section-title">推薦關係</div>
                            <table class="info-table">
                                <tbody>
                                    <tr>
                                        <td class="info-label">推薦人</td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing"
                                                v-model:value="editForm.referral_id"
                                                show-search
                                                :filter-option="false"
                                                :options="referralOptions"
                                                :loading="referralLoading"
                                                placeholder="搜尋並選擇推薦人"
                                                style="width: 100%"
                                                allow-clear
                                                @search="onReferralSearch"
                                            />
                                            <span v-else>
                                                {{ memberDetail.referral || '-' }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">推薦事業部</td>
                                        <td class="info-value">
                                            {{
                                                memberDetail.referral_department ||
                                                '-'
                                            }}
                                            <div
                                                v-if="isEditing"
                                                style="margin-top: 4px; color: #8a8aa0; font-size: 12px"
                                            >
                                                儲存後依新推薦人的推薦線重算（第一個紫星以上的輔導事業部）
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 夥伴組織（需 100118） -->
                        <div v-if="canViewPartnerOrg" class="info-section">
                            <div class="section-title">夥伴組織</div>
                            <table class="info-table">
                                <tbody>
                                    <tr>
                                        <td class="info-label">聘階</td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing && canEditPartnerOrg"
                                                v-model:value="editForm.rank_id"
                                                :options="rankOptions"
                                                placeholder="請選擇聘階"
                                                style="width: 100%"
                                                allow-clear
                                                :disabled="!isPartner"
                                                :virtual="false"
                                                show-search
                                                option-filter-prop="label"
                                            />
                                            <span v-else>
                                                {{ memberDetail.rank || '-' }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">
                                            <span class="org-field-label">
                                                輔導事業部
                                                <a
                                                    v-if="isEditing && canEditPartnerOrg"
                                                    class="org-copy-link"
                                                    :class="{
                                                        'is-disabled':
                                                            !editForm.mentor_id
                                                    }"
                                                    href="#"
                                                    @click.prevent="
                                                        copyEditMentorDeptFromMentor
                                                    "
                                                >
                                                    同輔導人
                                                </a>
                                            </span>
                                        </td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing && canEditPartnerOrg"
                                                v-model:value="
                                                    editForm.department_id
                                                "
                                                :options="departmentOptions"
                                                placeholder="請選擇輔導事業部"
                                                style="width: 100%"
                                                allow-clear
                                                :virtual="false"
                                                show-search
                                                option-filter-prop="label"
                                            />
                                            <span v-else>
                                                {{
                                                    memberDetail.department ||
                                                    '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">
                                            <span class="org-field-label">
                                                推薦事業部
                                                <a
                                                    v-if="isEditing && canEditPartnerOrg"
                                                    class="org-copy-link"
                                                    :class="{
                                                        'is-disabled':
                                                            !editForm.referral_id
                                                    }"
                                                    href="#"
                                                    @click.prevent="
                                                        copyEditReferralDeptFromReferrer
                                                    "
                                                >
                                                    同推薦人
                                                </a>
                                            </span>
                                        </td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing && canEditPartnerOrg"
                                                v-model:value="
                                                    editForm.referral_department_id
                                                "
                                                :options="departmentOptions"
                                                placeholder="請選擇推薦事業部"
                                                style="width: 100%"
                                                allow-clear
                                                :virtual="false"
                                                show-search
                                                option-filter-prop="label"
                                            />
                                            <span v-else>
                                                {{
                                                    memberDetail.referral_department ||
                                                    '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">推薦人</td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing && canEditPartnerOrg"
                                                v-model:value="
                                                    editForm.referral_id
                                                "
                                                show-search
                                                :filter-option="false"
                                                :options="referralOptions"
                                                :loading="referralLoading"
                                                placeholder="搜尋並選擇推薦人"
                                                style="width: 100%"
                                                allow-clear
                                                @search="onReferralSearch"
                                            />
                                            <span v-else>
                                                {{
                                                    memberDetail.referral || '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">
                                            <span class="org-field-label">
                                                招募人
                                                <a
                                                    v-if="isEditing && canEditPartnerOrg"
                                                    class="org-copy-link"
                                                    :class="{
                                                        'is-disabled':
                                                            !editForm.referral_id
                                                    }"
                                                    href="#"
                                                    @click.prevent="
                                                        copyEditReferralToRecruiter
                                                    "
                                                >
                                                    同推薦人
                                                </a>
                                            </span>
                                        </td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing && canEditPartnerOrg"
                                                v-model:value="
                                                    editForm.recruiter_id
                                                "
                                                show-search
                                                :filter-option="false"
                                                :options="recruiterOptions"
                                                :loading="recruiterLoading"
                                                placeholder="搜尋並選擇招募人"
                                                style="width: 100%"
                                                allow-clear
                                                @search="onRecruiterSearch"
                                            />
                                            <span v-else>
                                                {{
                                                    memberDetail.recruiter || '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">輔導人</td>
                                        <td class="info-value">
                                            <a-select
                                                v-if="isEditing && canEditPartnerOrg"
                                                v-model:value="
                                                    editForm.mentor_id
                                                "
                                                show-search
                                                :filter-option="false"
                                                :options="mentorOptions"
                                                :loading="mentorLoading"
                                                placeholder="搜尋並選擇輔導人"
                                                style="width: 100%"
                                                allow-clear
                                                :disabled="!isPartner"
                                                @search="onMentorSearch"
                                            />
                                            <span v-else>
                                                {{ memberDetail.mentor || '-' }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">起聘日期</td>
                                        <td class="info-value">
                                            <a-date-picker
                                                v-if="isEditing && canEditPartnerOrg"
                                                v-model:value="
                                                    editForm.assigned_at
                                                "
                                                placeholder="請選擇起聘日期"
                                                style="width: 100%"
                                                format="YYYY-MM-DD"
                                                value-format="YYYY-MM-DD"
                                                :disabled="!isPartner"
                                            />
                                            <span v-else>
                                                {{
                                                    (memberDetail.partner_assigned_at &&
                                                        String(
                                                            memberDetail.partner_assigned_at
                                                        ).slice(0, 10)) ||
                                                    '-'
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- 遊戲資訊區塊 -->
                        <div class="info-section">
                            <div class="section-title">遊戲資訊</div>
                            <table class="info-table">
                                <tbody>
                                    <tr>
                                        <td class="info-label">等級</td>
                                        <td class="info-value">
                                            Lv. {{ memberDetail.level || 1 }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="info-label">建立時間</td>
                                        <td class="info-value">
                                            {{ memberDetail.created_at || '-' }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div
                        v-else-if="activeTab === 'detail' && !memberDetail"
                        class="no-data"
                    >
                        <p>無法載入會員資料</p>
                    </div>

                    <!-- 會員可用系統 -->
                    <div
                        v-else-if="activeTab === 'system_access'"
                        class="record-content record-content--system-access w-full"
                    >
                        <div class="system-access-layout">
                            <p class="system-access-hint">
                                變更後請按儲存。消勾身分＝停用對應後台能力，不會刪除帳號（諮詢歷史仍可對照）。
                            </p>
                            <p class="system-access-hint system-access-hint--sub">
                                「管理」＝資產負債版本／設置／預約諮詢（與預約排班「管理」連動；一邊拿掉會同步另一邊）。角色管理與管理者管理請在
                                WealthOS 後台將帳號升級為「系統管理員」後才會出現。
                            </p>
                            <a-spin v-if="systemAccessLoading" />
                            <template v-else>
                                <section
                                    v-for="(
                                        sys, idx
                                    ) in SYSTEM_ACCESS_DEFINITIONS"
                                    :key="sys.code"
                                    class="system-access-zone"
                                >
                                    <header class="system-access-zone__head">
                                        <span class="system-access-zone__num">
                                            {{
                                                SYSTEM_ACCESS_ZONE_MARKS[idx] ??
                                                idx + 1
                                            }}
                                        </span>
                                        <div class="system-access-zone__titles">
                                            <span
                                                class="system-access-zone__title"
                                            >
                                                {{ sys.title }}
                                            </span>
                                        </div>
                                    </header>
                                    <div class="system-access-zone__body">
                                        <a-checkbox
                                            v-model:checked="
                                                systemAccessForm[sys.code]
                                                    .enabled
                                            "
                                            :disabled="
                                                !canEdit || systemAccessSaving
                                            "
                                            class="system-access-enable-cb"
                                        >
                                            <span
                                                class="text-sm text-slate-800"
                                            >
                                                開通
                                            </span>
                                        </a-checkbox>
                                        <template
                                            v-if="
                                                systemAccessForm[sys.code]
                                                    .enabled
                                            "
                                        >
                                            <div
                                                class="system-access-zone__divider"
                                            />
                                            <div class="system-access-label">
                                                身份
                                            </div>
                                            <a-checkbox-group
                                                v-model:value="
                                                    systemAccessForm[sys.code]
                                                        .roles
                                                "
                                                :options="sys.roleOptions"
                                                :disabled="
                                                    !canEdit ||
                                                    systemAccessSaving
                                                "
                                                class="system-access-role-group"
                                            />
                                        </template>
                                        <template v-else>
                                            <div
                                                class="system-access-zone__divider"
                                            />
                                            <p class="system-access-muted">
                                                未開通
                                            </p>
                                        </template>
                                    </div>
                                </section>
                                <div class="system-access-actions">
                                    <a-button
                                        v-if="canEdit"
                                        type="primary"
                                        class="vaporwave-button system-access-save-btn"
                                        :loading="systemAccessSaving"
                                        @click="saveSystemAccess"
                                    >
                                        儲存
                                    </a-button>
                                </div>
                            </template>
                        </div>
                    </div>

                    <!-- 活動參加紀錄 -->
                    <div
                        v-else-if="activeTab === 'activity_history'"
                        class="record-content w-full"
                    >
                        <AttendeeManagement
                            :embedded="true"
                            :user-id="activityHistoryUserId"
                        />
                    </div>

                    <!-- 積分紀錄 -->
                    <div
                        v-else-if="activeTab === 'points'"
                        class="record-content w-full"
                    >
                        <div class="info-section w-full">
                            <div
                                class="mb-4 grid gap-3 md:grid-cols-4 grid-cols-1"
                            >
                                <a-select
                                    v-model:value="pointFilters.point_type"
                                    :options="pointTypeOptions"
                                    allow-clear
                                    placeholder="選擇積分類型"
                                    size="middle"
                                    class="status-select"
                                />
                                <a-date-picker
                                    v-model:value="pointFilters.created_from"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="起始日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <a-date-picker
                                    v-model:value="pointFilters.created_to"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="結束日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <div class="flex gap-2 items-center">
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="onSearchPointLogs"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="onResetPointFilters"
                                    >
                                        重置
                                    </a-button>
                                </div>
                            </div>
                            <DataTable
                                :no-card="true"
                                title=""
                                :columns="pointColumns"
                                :data-source="pointLogs"
                                :pagination="pointPagination"
                                :show-pagination="true"
                                row-key="id"
                                @change="onPointTableChange"
                                @refresh="refreshPointLogs"
                            />
                        </div>
                    </div>

                    <!-- 等級紀錄 -->
                    <div
                        v-else-if="activeTab === 'level'"
                        class="record-content w-full"
                    >
                        <div class="info-section w-full">
                            <div
                                class="mb-4 grid gap-3 md:grid-cols-3 grid-cols-1"
                            >
                                <a-date-picker
                                    v-model:value="levelFilters.upgraded_from"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="起始日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <a-date-picker
                                    v-model:value="levelFilters.upgraded_to"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="結束日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <div class="flex gap-2 items-center">
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="onSearchLevelLogs"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="onResetLevelFilters"
                                    >
                                        重置
                                    </a-button>
                                </div>
                            </div>
                            <DataTable
                                :no-card="true"
                                title=""
                                :columns="levelColumns"
                                :data-source="levelLogs"
                                :pagination="levelPagination"
                                :show-pagination="true"
                                row-key="id"
                                @change="onLevelTableChange"
                                @refresh="refreshLevelLogs"
                            >
                                <template #level_change="{ record }">
                                    <span class="level-change">
                                        Lv.{{ record.from_level }} → Lv.{{
                                            record.to_level
                                        }}
                                    </span>
                                </template>
                                <template #role="{ record }">
                                    <span v-if="record.role_name">
                                        {{ record.role_name }}
                                    </span>
                                    <span v-else>-</span>
                                </template>
                                <template #rank="{ record }">
                                    <span v-if="record.rank_name">
                                        {{ record.rank_name }}
                                    </span>
                                    <span v-else>-</span>
                                </template>
                                <template #status="{ text }">
                                    <a-tag
                                        :color="
                                            text === '成功'
                                                ? 'green'
                                                : text === '失敗'
                                                  ? 'red'
                                                  : 'default'
                                        "
                                    >
                                        {{ text }}
                                    </a-tag>
                                </template>
                                <template #upgraded_at="{ text }">
                                    {{ text ? formatDateTime(text) : '-' }}
                                </template>
                            </DataTable>
                        </div>
                    </div>

                    <!-- 任務紀錄 -->
                    <div
                        v-else-if="activeTab === 'task'"
                        class="record-content task-records-content"
                    >
                        <!-- 工具欄 -->
                        <div class="task-toolbar">
                            <a-collapse
                                v-model:activeKey="taskFilterCollapseActiveKey"
                                :bordered="false"
                                class="task-filter-collapse"
                            >
                                <a-collapse-panel
                                    key="filters"
                                    :showArrow="false"
                                >
                                    <template #header>
                                        <div class="task-filter-header">
                                            <span
                                                class="task-filter-header-title"
                                            >
                                                <FilterOutlined />
                                                篩選條件
                                            </span>
                                            <a-badge
                                                :count="
                                                    getTaskActiveFilterCount()
                                                "
                                                :number-style="{
                                                    backgroundColor: '#2a4a7a'
                                                }"
                                            />
                                        </div>
                                    </template>

                                    <div class="task-filters-content">
                                        <!-- 基本篩選 -->
                                        <div class="task-filter-group">
                                            <div
                                                class="task-filter-group-title"
                                            >
                                                基本篩選
                                            </div>
                                            <div class="task-filter-row">
                                                <a-select
                                                    v-model:value="
                                                        taskFilters.status
                                                    "
                                                    placeholder="狀態"
                                                    allow-clear
                                                    size="middle"
                                                    class="status-select task-filter-item"
                                                    :options="taskStatusOptions"
                                                    @change="
                                                        handleTaskFilterChange
                                                    "
                                                />
                                                <a-range-picker
                                                    v-model:value="
                                                        taskDateRange
                                                    "
                                                    size="middle"
                                                    class="status-select task-filter-item"
                                                    format="YYYY-MM-DD"
                                                    :placeholder="[
                                                        '開始日期',
                                                        '結束日期'
                                                    ]"
                                                    @change="
                                                        handleTaskDateRangeChange
                                                    "
                                                />
                                            </div>
                                        </div>

                                        <!-- 任務樹篩選 -->
                                        <div class="task-filter-group">
                                            <div
                                                class="task-filter-group-title"
                                            >
                                                任務樹篩選
                                            </div>
                                            <div class="task-filter-row">
                                                <a-select
                                                    v-model:value="
                                                        taskNodePath.missionId
                                                    "
                                                    placeholder="主線"
                                                    allow-clear
                                                    size="middle"
                                                    class="status-select task-filter-item"
                                                    :options="
                                                        taskMissionSelectOptions
                                                    "
                                                    :loading="
                                                        taskLoadingMissions
                                                    "
                                                    show-search
                                                    option-filter-prop="label"
                                                    @change="
                                                        handleTaskMissionChange
                                                    "
                                                    @clear="
                                                        handleTaskNodePathClear
                                                    "
                                                />
                                                <a-select
                                                    v-model:value="
                                                        taskNodePath.chapterId
                                                    "
                                                    placeholder="章節"
                                                    allow-clear
                                                    size="middle"
                                                    class="status-select task-filter-item"
                                                    :options="
                                                        taskChapterSelectOptions
                                                    "
                                                    :loading="
                                                        taskLoadingChapters
                                                    "
                                                    :disabled="
                                                        !taskNodePath.missionId
                                                    "
                                                    show-search
                                                    option-filter-prop="label"
                                                    @change="
                                                        handleTaskChapterChange
                                                    "
                                                    @clear="
                                                        handleTaskNodePathClear
                                                    "
                                                />
                                                <a-select
                                                    v-model:value="
                                                        taskNodePath.taskId
                                                    "
                                                    placeholder="任務"
                                                    allow-clear
                                                    size="middle"
                                                    class="status-select task-filter-item"
                                                    :options="
                                                        taskTaskSelectOptions
                                                    "
                                                    :loading="taskLoadingTasks"
                                                    :disabled="
                                                        !taskNodePath.chapterId
                                                    "
                                                    show-search
                                                    option-filter-prop="label"
                                                    @change="
                                                        handleTaskTaskChange
                                                    "
                                                    @clear="
                                                        handleTaskNodePathClear
                                                    "
                                                />
                                                <a-select
                                                    v-model:value="
                                                        taskNodePath.stepId
                                                    "
                                                    placeholder="步驟"
                                                    allow-clear
                                                    size="middle"
                                                    class="status-select task-filter-item"
                                                    :options="
                                                        taskStepSelectOptions
                                                    "
                                                    :loading="taskLoadingSteps"
                                                    :disabled="
                                                        !taskNodePath.taskId
                                                    "
                                                    show-search
                                                    option-filter-prop="label"
                                                    @change="
                                                        handleTaskStepChange
                                                    "
                                                    @clear="
                                                        handleTaskNodePathClear
                                                    "
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </a-collapse-panel>
                            </a-collapse>

                            <!-- 操作按鈕區 -->
                            <div class="task-toolbar-actions">
                                <a-radio-group
                                    v-model:value="taskViewMode"
                                    button-style="solid"
                                    size="middle"
                                    class="task-view-mode-switch"
                                >
                                    <a-radio-button value="list">
                                        <UnorderedListOutlined />
                                        記錄列表
                                    </a-radio-button>
                                    <a-radio-button value="tree">
                                        <ApartmentOutlined />
                                        樹狀統計
                                    </a-radio-button>
                                </a-radio-group>
                            </div>
                        </div>

                        <!-- 記錄列表視圖 -->
                        <div
                            v-if="taskViewMode === 'list'"
                            class="task-records-list-view"
                        >
                            <DataTable
                                :columns="taskProgressColumns"
                                :data-source="taskProgressList"
                                :pagination="taskProgressPagination"
                                :loading="taskLoading"
                                :no-card="true"
                                @refresh="loadTaskProgressList"
                                @change="handleTaskProgressTableChange"
                                class="task-progress-table"
                            >
                                <template #bodyCell="{ column, record }">
                                    <template v-if="column.key === 'node'">
                                        <div
                                            class="task-node-cell"
                                            @click.stop="
                                                handleProgressRecordClick(
                                                    record
                                                )
                                            "
                                        >
                                            <a-tag
                                                :color="
                                                    getTaskNodeTypeColor(
                                                        record.node?.node_type
                                                    )
                                                "
                                                size="small"
                                            >
                                                {{
                                                    getTaskNodeTypeLabel(
                                                        record.node?.node_type
                                                    )
                                                }}
                                            </a-tag>
                                            <div class="task-node-name">
                                                {{ record.node?.name || '-' }}
                                            </div>
                                        </div>
                                    </template>
                                    <template
                                        v-else-if="column.key === 'hierarchy'"
                                    >
                                        <div
                                            v-if="record.node?.hierarchy"
                                            class="task-hierarchy-cell"
                                        >
                                            <div
                                                v-if="
                                                    record.node.hierarchy
                                                        .mission
                                                "
                                                class="task-hierarchy-item task-hierarchy-item-clickable"
                                                @click.stop="
                                                    handleHierarchyNodeClick(
                                                        'mission',
                                                        record.node.hierarchy
                                                            .mission.id,
                                                        undefined,
                                                        undefined,
                                                        undefined,
                                                        record.node.hierarchy
                                                            .mission.name
                                                    )
                                                "
                                            >
                                                <a-tag
                                                    color="purple"
                                                    size="small"
                                                >
                                                    主線
                                                </a-tag>
                                                <span
                                                    class="task-hierarchy-value"
                                                >
                                                    {{
                                                        record.node.hierarchy
                                                            .mission.name
                                                    }}
                                                </span>
                                            </div>
                                            <div
                                                v-if="
                                                    record.node.hierarchy
                                                        .chapter
                                                "
                                                class="task-hierarchy-item task-hierarchy-item-clickable"
                                                @click.stop="
                                                    handleHierarchyNodeClick(
                                                        'chapter',
                                                        record.node.hierarchy
                                                            .chapter.id,
                                                        record.node.hierarchy
                                                            .mission.id,
                                                        undefined,
                                                        undefined,
                                                        record.node.hierarchy
                                                            .chapter.name
                                                    )
                                                "
                                            >
                                                <a-tag
                                                    color="green"
                                                    size="small"
                                                >
                                                    章節
                                                </a-tag>
                                                <span
                                                    class="task-hierarchy-value"
                                                >
                                                    {{
                                                        record.node.hierarchy
                                                            .chapter.name
                                                    }}
                                                </span>
                                            </div>
                                            <div
                                                v-if="
                                                    record.node.hierarchy.task
                                                "
                                                class="task-hierarchy-item task-hierarchy-item-clickable"
                                                @click.stop="
                                                    handleHierarchyNodeClick(
                                                        'task',
                                                        record.node.hierarchy
                                                            .task.id,
                                                        record.node.hierarchy
                                                            .mission.id,
                                                        record.node.hierarchy
                                                            .chapter.id,
                                                        undefined,
                                                        record.node.hierarchy
                                                            .task.name
                                                    )
                                                "
                                            >
                                                <a-tag
                                                    color="blue"
                                                    size="small"
                                                >
                                                    任務
                                                </a-tag>
                                                <span
                                                    class="task-hierarchy-value"
                                                >
                                                    {{
                                                        record.node.hierarchy
                                                            .task.name
                                                    }}
                                                </span>
                                            </div>
                                            <div
                                                v-if="
                                                    record.node.hierarchy.step
                                                "
                                                class="task-hierarchy-item task-hierarchy-item-clickable"
                                                @click.stop="
                                                    handleHierarchyNodeClick(
                                                        'step',
                                                        record.node.hierarchy
                                                            .step.id,
                                                        record.node.hierarchy
                                                            .mission.id,
                                                        record.node.hierarchy
                                                            .chapter.id,
                                                        record.node.hierarchy
                                                            .task.id,
                                                        record.node.hierarchy
                                                            .step.name
                                                    )
                                                "
                                            >
                                                <a-tag
                                                    color="cyan"
                                                    size="small"
                                                >
                                                    步驟
                                                </a-tag>
                                                <span
                                                    class="task-hierarchy-value"
                                                >
                                                    {{
                                                        record.node.hierarchy
                                                            .step.name
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                        <span v-else>-</span>
                                    </template>
                                    <template
                                        v-else-if="column.key === 'status'"
                                    >
                                        <a-tag
                                            :color="
                                                getTaskStatusColor(
                                                    record.status
                                                )
                                            "
                                        >
                                            {{
                                                getTaskStatusLabel(
                                                    record.status
                                                )
                                            }}
                                        </a-tag>
                                    </template>
                                    <template
                                        v-else-if="
                                            column.key === 'conditions_summary'
                                        "
                                    >
                                        <div
                                            v-if="record.conditions_summary"
                                            class="task-conditions-summary"
                                        >
                                            <span class="task-summary-text">
                                                {{
                                                    record.conditions_summary
                                                        .completed
                                                }}
                                                /
                                                {{
                                                    record.conditions_summary
                                                        .total
                                                }}
                                            </span>
                                            <span
                                                v-if="
                                                    record.conditions_summary
                                                        .pending_review > 0
                                                "
                                                class="task-pending-review clickable"
                                                @click="
                                                    handleGoToTaskReviews(
                                                        record
                                                    )
                                                "
                                            >
                                                待審核：{{
                                                    record.conditions_summary
                                                        .pending_review
                                                }}
                                            </span>
                                        </div>
                                        <span v-else>-</span>
                                    </template>
                                    <template
                                        v-else-if="column.key === 'started_at'"
                                    >
                                        {{
                                            formatTaskDateTime(
                                                record.started_at
                                            ) || '-'
                                        }}
                                    </template>
                                    <template
                                        v-else-if="
                                            column.key === 'completed_at'
                                        "
                                    >
                                        {{
                                            formatTaskDateTime(
                                                record.completed_at
                                            ) || '-'
                                        }}
                                    </template>
                                </template>
                            </DataTable>
                        </div>

                        <!-- 樹狀統計視圖 -->
                        <div
                            v-if="taskViewMode === 'tree'"
                            class="task-tree-stats-view"
                        >
                            <div
                                v-if="taskLoading"
                                class="task-loading-container"
                            >
                                <a-spin size="large" />
                            </div>
                            <div
                                v-else-if="taskTreeStatsData.length === 0"
                                class="task-empty-container"
                            >
                                <a-empty description="暫無資料" />
                            </div>
                            <div v-else class="task-mission-grid">
                                <div
                                    v-for="mission in taskTreeStatsData"
                                    :key="mission.node.id"
                                    class="task-mission-card"
                                    :class="{
                                        'task-mission-card-expanded':
                                            isMissionExpanded(mission.node.id)
                                    }"
                                >
                                    <!-- 主線標題 -->
                                    <div class="task-mission-header">
                                        <div class="task-mission-title">
                                            <a-button
                                                type="text"
                                                size="small"
                                                class="task-mission-expand-btn"
                                                @click.stop="
                                                    toggleMissionExpand(
                                                        mission.node.id
                                                    )
                                                "
                                            >
                                                <template #icon>
                                                    <CaretDownOutlined
                                                        v-if="
                                                            !isMissionExpanded(
                                                                mission.node.id
                                                            )
                                                        "
                                                    />
                                                    <CaretUpOutlined v-else />
                                                </template>
                                            </a-button>
                                            <a-tag
                                                :color="
                                                    getTaskNodeTypeColor(
                                                        mission.node?.node_type
                                                    )
                                                "
                                                size="small"
                                                class="task-mission-tag"
                                            >
                                                {{
                                                    getTaskNodeTypeLabel(
                                                        mission.node?.node_type
                                                    )
                                                }}
                                            </a-tag>
                                            <h3 class="task-mission-name">
                                                {{ mission.node?.name || '-' }}
                                            </h3>
                                        </div>
                                        <div class="task-mission-status">
                                            <a-tag
                                                v-if="
                                                    getNodeProgressStatus(
                                                        mission
                                                    )
                                                "
                                                :color="
                                                    getTaskStatusColor(
                                                        getNodeProgressStatus(
                                                            mission
                                                        )
                                                    )
                                                "
                                                size="small"
                                                :class="{
                                                    'task-status-tag-clickable':
                                                        getNodeProgressStatus(
                                                            mission
                                                        ) !== 'not_started'
                                                }"
                                                @click.stop="
                                                    getNodeProgressStatus(
                                                        mission
                                                    ) !== 'not_started' &&
                                                    handleNodeClick(
                                                        mission.node,
                                                        mission
                                                    )
                                                "
                                            >
                                                {{
                                                    getTaskStatusLabel(
                                                        getNodeProgressStatus(
                                                            mission
                                                        )
                                                    )
                                                }}
                                            </a-tag>
                                            <span
                                                v-if="
                                                    getNodeProgressTime(mission)
                                                "
                                                class="task-mission-time"
                                            >
                                                {{
                                                    getNodeProgressTime(mission)
                                                }}
                                            </span>
                                        </div>
                                    </div>

                                    <!-- 收合狀態提示 -->
                                    <div
                                        v-if="
                                            !isMissionExpanded(mission.node.id)
                                        "
                                        class="task-mission-collapsed-hint"
                                    >
                                        <span class="hint-text">
                                            點擊展開按鈕查看詳細內容
                                        </span>
                                    </div>

                                    <!-- 樹狀結構 -->
                                    <div
                                        v-show="
                                            isMissionExpanded(
                                                mission.node.id
                                            ) &&
                                            mission.chapters &&
                                            mission.chapters.length > 0
                                        "
                                        class="task-tree-container"
                                    >
                                        <div
                                            v-for="chapter in mission.chapters"
                                            :key="chapter.node.id"
                                            class="task-tree-node-item"
                                        >
                                            <div
                                                class="task-tree-node-row task-tree-level-1"
                                                :class="{
                                                    'task-tree-node-clickable':
                                                        getNodeProgressStatus(
                                                            chapter
                                                        )
                                                }"
                                                @click="
                                                    handleNodeClick(
                                                        chapter.node,
                                                        chapter
                                                    )
                                                "
                                            >
                                                <div
                                                    class="task-tree-node-info"
                                                >
                                                    <a-tag
                                                        :color="
                                                            getTaskNodeTypeColor(
                                                                chapter.node
                                                                    ?.node_type
                                                            )
                                                        "
                                                        size="small"
                                                        class="task-tree-node-tag"
                                                    >
                                                        {{
                                                            getTaskNodeTypeLabel(
                                                                chapter.node
                                                                    ?.node_type
                                                            )
                                                        }}
                                                    </a-tag>
                                                    <span
                                                        class="task-tree-node-name"
                                                    >
                                                        {{
                                                            chapter.node
                                                                ?.name || '-'
                                                        }}
                                                    </span>
                                                </div>
                                                <div
                                                    class="task-tree-node-status"
                                                >
                                                    <a-tag
                                                        v-if="
                                                            getNodeProgressStatus(
                                                                chapter
                                                            )
                                                        "
                                                        :color="
                                                            getTaskStatusColor(
                                                                getNodeProgressStatus(
                                                                    chapter
                                                                )
                                                            )
                                                        "
                                                        size="small"
                                                    >
                                                        {{
                                                            getTaskStatusLabel(
                                                                getNodeProgressStatus(
                                                                    chapter
                                                                )
                                                            )
                                                        }}
                                                    </a-tag>
                                                    <span
                                                        v-if="
                                                            getNodeProgressTime(
                                                                chapter
                                                            )
                                                        "
                                                        class="task-tree-node-time"
                                                    >
                                                        {{
                                                            getNodeProgressTime(
                                                                chapter
                                                            )
                                                        }}
                                                    </span>
                                                </div>
                                            </div>
                                            <div
                                                v-if="
                                                    chapter.tasks &&
                                                    chapter.tasks.length > 0
                                                "
                                                class="task-tree-children"
                                            >
                                                <div
                                                    v-for="task in chapter.tasks"
                                                    :key="task.node.id"
                                                    class="task-tree-node-item"
                                                >
                                                    <div
                                                        class="task-tree-node-row task-tree-level-2"
                                                        :class="{
                                                            'task-tree-node-clickable':
                                                                getNodeProgressStatus(
                                                                    task
                                                                )
                                                        }"
                                                        @click="
                                                            handleNodeClick(
                                                                task.node,
                                                                task
                                                            )
                                                        "
                                                    >
                                                        <div
                                                            class="task-tree-node-info"
                                                        >
                                                            <a-tag
                                                                :color="
                                                                    getTaskNodeTypeColor(
                                                                        task
                                                                            .node
                                                                            ?.node_type
                                                                    )
                                                                "
                                                                size="small"
                                                                class="task-tree-node-tag"
                                                            >
                                                                {{
                                                                    getTaskNodeTypeLabel(
                                                                        task
                                                                            .node
                                                                            ?.node_type
                                                                    )
                                                                }}
                                                            </a-tag>
                                                            <span
                                                                class="task-tree-node-name"
                                                            >
                                                                {{
                                                                    task.node
                                                                        ?.name ||
                                                                    '-'
                                                                }}
                                                            </span>
                                                        </div>
                                                        <div
                                                            class="task-tree-node-status"
                                                        >
                                                            <a-tag
                                                                v-if="
                                                                    getNodeProgressStatus(
                                                                        task
                                                                    )
                                                                "
                                                                :color="
                                                                    getTaskStatusColor(
                                                                        getNodeProgressStatus(
                                                                            task
                                                                        )
                                                                    )
                                                                "
                                                                size="small"
                                                            >
                                                                {{
                                                                    getTaskStatusLabel(
                                                                        getNodeProgressStatus(
                                                                            task
                                                                        )
                                                                    )
                                                                }}
                                                            </a-tag>
                                                            <span
                                                                v-if="
                                                                    getNodeProgressTime(
                                                                        task
                                                                    )
                                                                "
                                                                class="task-tree-node-time"
                                                            >
                                                                {{
                                                                    getNodeProgressTime(
                                                                        task
                                                                    )
                                                                }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div
                                                        v-if="
                                                            task.steps &&
                                                            task.steps.length >
                                                                0
                                                        "
                                                        class="task-tree-children"
                                                    >
                                                        <div
                                                            v-for="step in task.steps"
                                                            :key="step.node.id"
                                                            class="task-tree-node-item"
                                                        >
                                                            <div
                                                                class="task-tree-node-row task-tree-level-3"
                                                                :class="{
                                                                    'task-tree-node-clickable':
                                                                        getNodeProgressStatus(
                                                                            step
                                                                        )
                                                                }"
                                                                @click="
                                                                    handleNodeClick(
                                                                        step.node,
                                                                        step
                                                                    )
                                                                "
                                                            >
                                                                <div
                                                                    class="task-tree-node-info"
                                                                >
                                                                    <a-tag
                                                                        :color="
                                                                            getTaskNodeTypeColor(
                                                                                step
                                                                                    .node
                                                                                    ?.node_type
                                                                            )
                                                                        "
                                                                        size="small"
                                                                        class="task-tree-node-tag"
                                                                    >
                                                                        {{
                                                                            getTaskNodeTypeLabel(
                                                                                step
                                                                                    .node
                                                                                    ?.node_type
                                                                            )
                                                                        }}
                                                                    </a-tag>
                                                                    <span
                                                                        class="task-tree-node-name"
                                                                    >
                                                                        {{
                                                                            step
                                                                                .node
                                                                                ?.name ||
                                                                            '-'
                                                                        }}
                                                                    </span>
                                                                </div>
                                                                <div
                                                                    class="task-tree-node-status"
                                                                >
                                                                    <a-tag
                                                                        v-if="
                                                                            getNodeProgressStatus(
                                                                                step
                                                                            )
                                                                        "
                                                                        :color="
                                                                            getTaskStatusColor(
                                                                                getNodeProgressStatus(
                                                                                    step
                                                                                )
                                                                            )
                                                                        "
                                                                        size="small"
                                                                    >
                                                                        {{
                                                                            getTaskStatusLabel(
                                                                                getNodeProgressStatus(
                                                                                    step
                                                                                )
                                                                            )
                                                                        }}
                                                                    </a-tag>
                                                                    <span
                                                                        v-if="
                                                                            getNodeProgressTime(
                                                                                step
                                                                            )
                                                                        "
                                                                        class="task-tree-node-time"
                                                                    >
                                                                        {{
                                                                            getNodeProgressTime(
                                                                                step
                                                                            )
                                                                        }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 節點詳細歷程 Modal -->
                        <a-modal
                            v-model:open="taskNodeDetailModalVisible"
                            :title="taskNodeDetailModalTitle"
                            :width="1000"
                            :footer="null"
                            class="task-node-detail-modal vaporwave-modal"
                            @cancel="handleTaskNodeDetailModalCancel"
                        >
                            <div
                                v-if="taskNodeDetailData"
                                class="task-node-detail-content"
                            >
                                <!-- 節點基本資訊 -->
                                <div class="task-node-detail-info">
                                    <a-descriptions
                                        :column="2"
                                        bordered
                                        size="small"
                                        class="task-node-basic-info"
                                    >
                                        <a-descriptions-item label="節點名稱">
                                            {{
                                                taskNodeDetailData.node?.name ||
                                                '-'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="節點類型">
                                            <a-tag
                                                :color="
                                                    getTaskNodeTypeColor(
                                                        taskNodeDetailData.node
                                                            ?.node_type
                                                    )
                                                "
                                            >
                                                {{
                                                    getTaskNodeTypeLabel(
                                                        taskNodeDetailData.node
                                                            ?.node_type
                                                    )
                                                }}
                                            </a-tag>
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            v-if="taskNodeDetailData.node?.id"
                                            label="節點 ID"
                                        >
                                            {{ taskNodeDetailData.node.id }}
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            v-if="
                                                taskNodeDetailData.node
                                                    ?.description
                                            "
                                            label="節點描述"
                                        >
                                            {{
                                                taskNodeDetailData.node
                                                    .description || '-'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            v-if="
                                                taskNodeDetailData.node
                                                    ?.hierarchy?.mission
                                            "
                                            label="所屬主線"
                                        >
                                            {{
                                                taskNodeDetailData.node
                                                    .hierarchy.mission.name
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            v-if="
                                                taskNodeDetailData.node
                                                    ?.hierarchy?.chapter
                                            "
                                            label="所屬章節"
                                        >
                                            {{
                                                taskNodeDetailData.node
                                                    .hierarchy.chapter.name
                                            }}
                                        </a-descriptions-item>
                                    </a-descriptions>
                                </div>

                                <!-- Tab 內容區 -->
                                <a-tabs
                                    v-model:activeKey="taskDetailActiveTab"
                                    type="card"
                                    class="task-detail-tabs"
                                >
                                    <!-- 進度記錄 Tab -->
                                    <a-tab-pane
                                        key="progress"
                                        :tab="`進度記錄 (${taskNodeDetailProgressList.length})`"
                                    >
                                        <div class="task-detail-tab-content">
                                            <div
                                                v-if="
                                                    taskNodeDetailProgressList.length ===
                                                    0
                                                "
                                                class="task-node-detail-empty"
                                            >
                                                <a-empty
                                                    description="暫無進度記錄"
                                                />
                                            </div>
                                            <div
                                                v-else
                                                class="task-node-detail-progress-list"
                                            >
                                                <div
                                                    v-for="(
                                                        progress, index
                                                    ) in taskNodeDetailProgressList"
                                                    :key="progress.id || index"
                                                    class="task-node-detail-progress-item"
                                                    :class="{
                                                        'task-progress-item-selected':
                                                            selectedProgressId ===
                                                            progress.id
                                                    }"
                                                    @click="
                                                        handleProgressItemClick(
                                                            progress.id
                                                        )
                                                    "
                                                >
                                                    <div
                                                        class="task-progress-item-header"
                                                    >
                                                        <a-tag
                                                            :color="
                                                                getTaskStatusColor(
                                                                    progress.status
                                                                )
                                                            "
                                                            size="small"
                                                        >
                                                            {{
                                                                getTaskStatusLabel(
                                                                    progress.status
                                                                )
                                                            }}
                                                        </a-tag>
                                                        <span
                                                            class="task-progress-item-time"
                                                        >
                                                            {{
                                                                formatTaskDateTime(
                                                                    progress.created_at ||
                                                                        progress.updated_at
                                                                ) || '-'
                                                            }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        v-if="
                                                            progress.conditions_summary
                                                        "
                                                        class="task-progress-item-conditions"
                                                    >
                                                        <span
                                                            class="task-progress-conditions-text"
                                                        >
                                                            已完成：{{
                                                                progress
                                                                    .conditions_summary
                                                                    .completed
                                                            }}
                                                            /
                                                            {{
                                                                progress
                                                                    .conditions_summary
                                                                    .total
                                                            }}
                                                        </span>
                                                        <span
                                                            v-if="
                                                                progress
                                                                    .conditions_summary
                                                                    .pending_review >
                                                                0
                                                            "
                                                            class="task-progress-pending-review clickable"
                                                            @click="
                                                                handleGoToTaskReviewsFromProgress(
                                                                    progress
                                                                )
                                                            "
                                                        >
                                                            待審核：{{
                                                                progress
                                                                    .conditions_summary
                                                                    .pending_review
                                                            }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        v-if="
                                                            progress.started_at
                                                        "
                                                        class="task-progress-item-time-info"
                                                    >
                                                        開始時間：{{
                                                            formatTaskDateTime(
                                                                progress.started_at
                                                            ) || '-'
                                                        }}
                                                    </div>
                                                    <div
                                                        v-if="
                                                            progress.completed_at
                                                        "
                                                        class="task-progress-item-time-info"
                                                    >
                                                        完成時間：{{
                                                            formatTaskDateTime(
                                                                progress.completed_at
                                                            ) || '-'
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </a-tab-pane>

                                    <!-- 詳細資訊 Tab（僅在選中記錄時顯示） -->
                                    <a-tab-pane
                                        v-if="selectedProgressId"
                                        key="basic"
                                        tab="詳細資訊"
                                    >
                                        <div class="task-detail-tab-content">
                                            <div
                                                v-if="
                                                    taskNodeDetailDetailsLoading
                                                "
                                                class="task-node-detail-loading"
                                            >
                                                <a-spin size="large" />
                                            </div>
                                            <div
                                                v-else-if="
                                                    taskNodeDetailDetails
                                                "
                                                class="task-node-detail-details-content"
                                            >
                                                <!-- 進度詳細資訊 -->
                                                <div
                                                    class="task-detail-section"
                                                >
                                                    <h4
                                                        class="task-node-detail-section-title"
                                                    >
                                                        進度詳細資訊
                                                    </h4>
                                                    <a-descriptions
                                                        :column="2"
                                                        bordered
                                                        size="small"
                                                        class="task-detail-descriptions"
                                                    >
                                                        <a-descriptions-item
                                                            label="狀態"
                                                        >
                                                            <a-tag
                                                                :color="
                                                                    getTaskStatusColor(
                                                                        taskNodeDetailDetails
                                                                            .progress
                                                                            ?.status
                                                                    )
                                                                "
                                                                class="status-tag"
                                                            >
                                                                {{
                                                                    getTaskStatusLabel(
                                                                        taskNodeDetailDetails
                                                                            .progress
                                                                            ?.status
                                                                    )
                                                                }}
                                                            </a-tag>
                                                        </a-descriptions-item>
                                                        <a-descriptions-item
                                                            label="持續時間"
                                                        >
                                                            {{
                                                                getProgressDuration(
                                                                    taskNodeDetailDetails
                                                                        .progress
                                                                        ?.started_at,
                                                                    taskNodeDetailDetails
                                                                        .progress
                                                                        ?.completed_at
                                                                )
                                                            }}
                                                        </a-descriptions-item>
                                                        <a-descriptions-item
                                                            label="開始時間"
                                                        >
                                                            <span
                                                                class="time-value"
                                                            >
                                                                {{
                                                                    formatTaskDateTime(
                                                                        taskNodeDetailDetails
                                                                            .progress
                                                                            ?.started_at
                                                                    ) || '-'
                                                                }}
                                                            </span>
                                                        </a-descriptions-item>
                                                        <a-descriptions-item
                                                            label="完成時間"
                                                        >
                                                            <span
                                                                class="time-value"
                                                            >
                                                                {{
                                                                    formatTaskDateTime(
                                                                        taskNodeDetailDetails
                                                                            .progress
                                                                            ?.completed_at
                                                                    ) || '-'
                                                                }}
                                                            </span>
                                                        </a-descriptions-item>
                                                        <a-descriptions-item
                                                            label="建立時間"
                                                        >
                                                            <span
                                                                class="time-value"
                                                            >
                                                                {{
                                                                    formatTaskDateTime(
                                                                        taskNodeDetailDetails
                                                                            .progress
                                                                            ?.created_at
                                                                    ) || '-'
                                                                }}
                                                            </span>
                                                        </a-descriptions-item>
                                                        <a-descriptions-item
                                                            label="更新時間"
                                                        >
                                                            <span
                                                                class="time-value"
                                                            >
                                                                {{
                                                                    formatTaskDateTime(
                                                                        taskNodeDetailDetails
                                                                            .progress
                                                                            ?.updated_at
                                                                    ) || '-'
                                                                }}
                                                            </span>
                                                        </a-descriptions-item>
                                                    </a-descriptions>
                                                </div>
                                            </div>
                                        </div>
                                    </a-tab-pane>

                                    <!-- 條件列表 Tab -->
                                    <a-tab-pane
                                        v-if="
                                            selectedProgressId &&
                                            taskNodeDetailDetails
                                        "
                                        key="conditions"
                                        :tab="`條件列表 (${taskNodeDetailDetails.conditions_summary?.total || 0})`"
                                    >
                                        <div class="task-detail-tab-content">
                                            <div
                                                v-if="
                                                    !taskNodeDetailDetails
                                                        .conditions?.length
                                                "
                                                class="task-detail-empty"
                                            >
                                                <a-empty
                                                    description="暫無條件記錄"
                                                />
                                            </div>
                                            <div
                                                v-else
                                                class="task-detail-conditions-list"
                                            >
                                                <div
                                                    v-for="condition in taskNodeDetailDetails.conditions"
                                                    :key="condition.id"
                                                    class="task-detail-condition-item"
                                                >
                                                    <div
                                                        class="task-condition-header"
                                                    >
                                                        <div
                                                            class="task-condition-info"
                                                        >
                                                            <a-tag
                                                                :color="
                                                                    condition.result_status ===
                                                                    'approved'
                                                                        ? 'success'
                                                                        : condition.result_status ===
                                                                            'rejected'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                "
                                                                size="small"
                                                                class="condition-type-tag"
                                                            >
                                                                {{
                                                                    condition.type_display ||
                                                                    condition.type ||
                                                                    '-'
                                                                }}
                                                            </a-tag>
                                                            <span
                                                                class="task-condition-description"
                                                            >
                                                                {{
                                                                    condition.code_name ||
                                                                    condition.description ||
                                                                    condition.code ||
                                                                    '-'
                                                                }}
                                                            </span>
                                                        </div>
                                                        <a-tag
                                                            v-if="
                                                                condition.result_status
                                                            "
                                                            :color="
                                                                condition.result_status ===
                                                                'approved'
                                                                    ? 'success'
                                                                    : condition.result_status ===
                                                                        'rejected'
                                                                      ? 'error'
                                                                      : 'warning'
                                                            "
                                                            size="small"
                                                            class="condition-status-tag"
                                                        >
                                                            {{
                                                                condition.result_status_display ||
                                                                condition.result_status ===
                                                                    'approved'
                                                                    ? '已通過'
                                                                    : condition.result_status ===
                                                                        'rejected'
                                                                      ? '已拒絕'
                                                                      : '待審核'
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                    <div
                                                        class="task-condition-details"
                                                    >
                                                        <div
                                                            v-if="
                                                                condition.submitted_at
                                                            "
                                                            class="task-condition-time"
                                                        >
                                                            <span
                                                                class="detail-label"
                                                            >
                                                                提交時間：
                                                            </span>
                                                            <span
                                                                class="detail-value"
                                                            >
                                                                {{
                                                                    formatTaskDateTime(
                                                                        condition.submitted_at
                                                                    ) || '-'
                                                                }}
                                                            </span>
                                                        </div>
                                                        <div
                                                            v-if="
                                                                condition.reviewed_by
                                                            "
                                                            class="task-condition-review"
                                                        >
                                                            <span
                                                                class="detail-label"
                                                            >
                                                                審核者：
                                                            </span>
                                                            <span
                                                                class="detail-value"
                                                            >
                                                                {{
                                                                    condition
                                                                        .reviewed_by
                                                                        ?.name ||
                                                                    condition
                                                                        .reviewed_by
                                                                        ?.email ||
                                                                    '-'
                                                                }}
                                                            </span>
                                                            <span
                                                                v-if="
                                                                    condition.reviewed_at
                                                                "
                                                                class="task-condition-review-time"
                                                            >
                                                                （{{
                                                                    formatTaskDateTime(
                                                                        condition.reviewed_at
                                                                    ) || '-'
                                                                }}）
                                                            </span>
                                                        </div>
                                                        <div
                                                            v-if="
                                                                condition.review_note
                                                            "
                                                            class="task-condition-note"
                                                        >
                                                            <span
                                                                class="detail-label"
                                                            >
                                                                審核備註：
                                                            </span>
                                                            <span
                                                                class="detail-value"
                                                            >
                                                                {{
                                                                    condition.review_note
                                                                }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </a-tab-pane>

                                    <!-- 時間線 Tab -->
                                    <a-tab-pane
                                        v-if="
                                            selectedProgressId &&
                                            taskNodeDetailDetails
                                        "
                                        key="timeline"
                                        :tab="`時間線 (${taskNodeDetailDetails.timeline?.length || 0})`"
                                    >
                                        <div class="task-detail-tab-content">
                                            <div
                                                v-if="
                                                    !taskNodeDetailDetails
                                                        .timeline?.length
                                                "
                                                class="task-detail-empty"
                                            >
                                                <a-empty
                                                    description="暫無時間線記錄"
                                                />
                                            </div>
                                            <div
                                                v-else
                                                class="task-detail-timeline"
                                            >
                                                <div
                                                    v-for="(
                                                        event, index
                                                    ) in taskNodeDetailDetails.timeline"
                                                    :key="index"
                                                    class="task-timeline-item"
                                                >
                                                    <div
                                                        class="task-timeline-dot"
                                                    ></div>
                                                    <div
                                                        class="task-timeline-content"
                                                    >
                                                        <div
                                                            class="task-timeline-label"
                                                        >
                                                            {{ event.label }}
                                                        </div>
                                                        <div
                                                            class="task-timeline-time"
                                                        >
                                                            {{
                                                                formatTaskDateTime(
                                                                    event.timestamp
                                                                ) || '-'
                                                            }}
                                                        </div>
                                                        <div
                                                            v-if="
                                                                event.description
                                                            "
                                                            class="task-timeline-description"
                                                        >
                                                            {{
                                                                event.description
                                                            }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </a-tab-pane>

                                    <!-- 關聯資訊 Tab -->
                                    <a-tab-pane
                                        v-if="
                                            selectedProgressId &&
                                            taskNodeDetailDetails
                                        "
                                        key="relations"
                                        tab="關聯資訊"
                                    >
                                        <div class="task-detail-tab-content">
                                            <!-- 觸發者資訊 -->
                                            <div
                                                v-if="
                                                    taskNodeDetailDetails.triggered_by
                                                "
                                                class="task-detail-section"
                                            >
                                                <h4
                                                    class="task-node-detail-section-title"
                                                >
                                                    觸發者
                                                </h4>
                                                <a-descriptions
                                                    :column="2"
                                                    bordered
                                                    size="small"
                                                    class="task-detail-descriptions"
                                                >
                                                    <a-descriptions-item
                                                        label="使用者"
                                                    >
                                                        {{
                                                            taskNodeDetailDetails
                                                                .triggered_by
                                                                .user?.name ||
                                                            taskNodeDetailDetails
                                                                .triggered_by
                                                                .user?.email ||
                                                            '-'
                                                        }}
                                                    </a-descriptions-item>
                                                    <a-descriptions-item
                                                        label="身份"
                                                    >
                                                        {{
                                                            taskNodeDetailDetails
                                                                .triggered_by
                                                                .user
                                                                ?.identity_name ||
                                                            '-'
                                                        }}
                                                    </a-descriptions-item>
                                                    <a-descriptions-item
                                                        v-if="
                                                            taskNodeDetailDetails
                                                                .triggered_by
                                                                .progress
                                                        "
                                                        label="觸發任務"
                                                    >
                                                        {{
                                                            taskNodeDetailDetails
                                                                .triggered_by
                                                                .progress.node
                                                                ?.name || '-'
                                                        }}
                                                    </a-descriptions-item>
                                                </a-descriptions>
                                            </div>

                                            <!-- 父進度 -->
                                            <div
                                                v-if="
                                                    taskNodeDetailDetails.parent_progress
                                                "
                                                class="task-detail-section"
                                            >
                                                <h4
                                                    class="task-node-detail-section-title"
                                                >
                                                    父進度
                                                </h4>
                                                <div
                                                    class="task-relation-progress-item"
                                                >
                                                    <div
                                                        class="relation-progress-name"
                                                    >
                                                        {{
                                                            taskNodeDetailDetails
                                                                .parent_progress
                                                                .node?.name ||
                                                            '-'
                                                        }}
                                                    </div>
                                                    <a-tag
                                                        :color="
                                                            getTaskStatusColor(
                                                                taskNodeDetailDetails
                                                                    .parent_progress
                                                                    .status
                                                            )
                                                        "
                                                        size="small"
                                                    >
                                                        {{
                                                            getTaskStatusLabel(
                                                                taskNodeDetailDetails
                                                                    .parent_progress
                                                                    .status
                                                            )
                                                        }}
                                                    </a-tag>
                                                </div>
                                            </div>

                                            <!-- 子進度列表 -->
                                            <div
                                                v-if="
                                                    taskNodeDetailDetails
                                                        .child_progresses
                                                        ?.length > 0
                                                "
                                                class="task-detail-section"
                                            >
                                                <h4
                                                    class="task-node-detail-section-title"
                                                >
                                                    子進度 ({{
                                                        taskNodeDetailDetails
                                                            .child_progresses
                                                            ?.length || 0
                                                    }})
                                                </h4>
                                                <div
                                                    class="task-detail-child-progresses"
                                                >
                                                    <div
                                                        v-for="child in taskNodeDetailDetails.child_progresses"
                                                        :key="child.id"
                                                        class="task-child-progress-item"
                                                    >
                                                        <div
                                                            class="task-child-progress-name"
                                                        >
                                                            {{
                                                                child.node
                                                                    ?.name ||
                                                                '-'
                                                            }}
                                                        </div>
                                                        <a-tag
                                                            :color="
                                                                getTaskStatusColor(
                                                                    child.status
                                                                )
                                                            "
                                                            size="small"
                                                        >
                                                            {{
                                                                getTaskStatusLabel(
                                                                    child.status
                                                                )
                                                            }}
                                                        </a-tag>
                                                        <div
                                                            v-if="
                                                                child.started_at
                                                            "
                                                            class="task-child-progress-time"
                                                        >
                                                            開始：{{
                                                                formatTaskDateTime(
                                                                    child.started_at
                                                                ) || '-'
                                                            }}
                                                        </div>
                                                        <div
                                                            v-if="
                                                                child.completed_at
                                                            "
                                                            class="task-child-progress-time"
                                                        >
                                                            完成：{{
                                                                formatTaskDateTime(
                                                                    child.completed_at
                                                                ) || '-'
                                                            }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                v-if="
                                                    !taskNodeDetailDetails.triggered_by &&
                                                    !taskNodeDetailDetails.parent_progress &&
                                                    (!taskNodeDetailDetails.child_progresses ||
                                                        taskNodeDetailDetails
                                                            .child_progresses
                                                            .length === 0)
                                                "
                                                class="task-detail-empty"
                                            >
                                                <a-empty
                                                    description="暫無關聯資訊"
                                                />
                                            </div>
                                        </div>
                                    </a-tab-pane>
                                </a-tabs>
                            </div>
                        </a-modal>
                    </div>

                    <!-- 推薦／招募／輔導紀錄 -->
                    <div
                        v-else-if="activeTab === 'referral'"
                        class="record-content w-full"
                    >
                        <div class="info-section w-full">
                            <div
                                class="mb-4 grid gap-3 md:grid-cols-4 grid-cols-1"
                            >
                                <a-select
                                    v-model:value="referralFilters.type"
                                    :options="referralTypeOptions"
                                    placeholder="選擇類型"
                                    size="middle"
                                    class="status-select"
                                />
                                <a-date-picker
                                    v-model:value="referralFilters.changed_from"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="變動起始日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <a-date-picker
                                    v-model:value="referralFilters.changed_to"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="變動結束日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <div class="flex gap-2 items-center">
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="onSearchReferralLogs"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="onResetReferralFilters"
                                    >
                                        重置
                                    </a-button>
                                </div>
                            </div>
                            <DataTable
                                :no-card="true"
                                title=""
                                :columns="referralColumns"
                                :data-source="referralLogs"
                                :pagination="referralPagination"
                                :show-pagination="true"
                                row-key="id"
                                @change="onReferralTableChange"
                                @refresh="refreshReferralLogs"
                            />
                        </div>
                    </div>

                    <!-- 事業部紀錄 -->
                    <div
                        v-else-if="activeTab === 'department'"
                        class="record-content w-full"
                    >
                        <div class="info-section w-full">
                            <div
                                class="mb-4 grid gap-3 md:grid-cols-3 grid-cols-1"
                            >
                                <a-date-picker
                                    v-model:value="
                                        departmentFilters.changed_from
                                    "
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="變動起始日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <a-date-picker
                                    v-model:value="departmentFilters.changed_to"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="變動結束日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <div class="flex gap-2 items-center">
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="onSearchDepartmentLogs"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="onResetDepartmentFilters"
                                    >
                                        重置
                                    </a-button>
                                </div>
                            </div>
                            <DataTable
                                :no-card="true"
                                title=""
                                :columns="departmentColumns"
                                :data-source="departmentLogs"
                                :pagination="departmentPagination"
                                :show-pagination="true"
                                row-key="id"
                                @change="onDepartmentTableChange"
                                @refresh="refreshDepartmentLogs"
                            />
                        </div>
                    </div>

                    <!-- 聘階紀錄 -->
                    <div
                        v-else-if="activeTab === 'rank'"
                        class="record-content w-full"
                    >
                        <div class="info-section w-full">
                            <div
                                class="mb-4 grid gap-3 md:grid-cols-3 grid-cols-1"
                            >
                                <a-date-picker
                                    v-model:value="rankFilters.assigned_from"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="異動起始日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <a-date-picker
                                    v-model:value="rankFilters.assigned_to"
                                    format="YYYY-MM-DD"
                                    value-format="YYYY-MM-DD"
                                    placeholder="異動結束日期"
                                    size="middle"
                                    class="status-select"
                                />
                                <div class="flex gap-2 items-center">
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="onSearchRankLogs"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="onResetRankFilters"
                                    >
                                        重置
                                    </a-button>
                                </div>
                            </div>
                            <DataTable
                                :no-card="true"
                                title=""
                                :columns="rankColumns"
                                :data-source="rankLogs"
                                :pagination="rankPagination"
                                :show-pagination="true"
                                row-key="id"
                                @change="onRankTableChange"
                                @refresh="refreshRankLogs"
                            />
                        </div>
                    </div>
                    <!-- 夥伴組織圖 Modal（樣式與事業部管理組織圖一致） -->
                    <a-modal
                        v-model:open="orgModalVisible"
                        title="夥伴組織圖"
                        :width="1000"
                        :footer="null"
                        destroy-on-close
                        wrapClassName="orgchart-modal vaporwave-modal"
                    >
                        <a-spin :spinning="orgChartLoading">
                            <div class="tree-section">
                                <h4 class="tree-section-title">組織樹狀圖</h4>
                                <div class="tree-container">
                                    <OrgChart
                                        :key="orgChartKey"
                                        :data="orgChartData"
                                        :line-type="orgLineType"
                                        @lineTypeChange="onOrgLineTypeChange"
                                    />
                                </div>
                            </div>
                        </a-spin>
                    </a-modal>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message, Modal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    ReloadOutlined,
    ApartmentOutlined,
    FilterOutlined,
    UnorderedListOutlined,
    CaretDownOutlined,
    CaretUpOutlined
} from '@ant-design/icons-vue';
import {
    getUserDetail,
    updateUser,
    searchUsers,
    getReferralLogs,
    getDepartmentLogs,
    getRankLogs,
    getUserOrganizationChart,
    getMemberSystemAccess,
    grantMemberSystemAccess,
    revokeMemberSystemAccess
} from '@/api/users';
import { fetchAllRankRows, toRankOptions } from '@/api/ranks';
import { fetchAllDepartmentRows, toDepartmentOptions } from '@/api/departments';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    blacklistUser,
    deregisterPartner,
    unblacklistUser
} from '@/api/legacyAccessControl';
import { canAccessMemberDetail } from '@/constants/permissionRules';
import {
    getIdentityName,
    getIdentityTagColor
} from '@/constants/memberIdentity.js';
import DataTable from '@/components/common/DataTable.vue';
import OrgChart from '@/components/common/OrgChart.vue';
import AttendeeManagement from '@/views/forms/AttendeeManagement.vue';
import { getPointLogs, getPointTypeOptions } from '@/api/points';
import { getLevelLogs } from '@/api/levels';
import {
    getMissionProgress,
    getMissionTreeStats,
    getNodeProgressUsers,
    getMissionDropdown,
    getChapterDropdown,
    getTaskDropdown,
    getStepDropdown,
    getUserMissionProgress,
    getUserMissionProgressDetails
} from '@/api/missions';
import formatDateTime from '@/utils/formatDateTime';
import dayjs from 'dayjs';
import {
    resolveMemberListBackRoute,
    getDetailProfileFromListRoute,
    MEMBER_DETAIL_TABS,
    canViewSensitivePersonalFields
} from '@/views/members/memberListProfiles.js';
import { isValidTaiwanIdNumber, TAIWAN_ID_LABEL } from '@/utils/taiwanIdNumber.js';

const route = useRoute();
const router = useRouter();
const adminStore = useAdminInfo();

const loading = ref(false);
const memberDetail = ref(null);
/** 路由上的會員 ID（活動參加紀錄內嵌需立刻帶入，不可等詳情載完） */
const memberId = computed(() => {
    const id = route.params.id;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
});
const activityHistoryUserId = computed(
    () => memberDetail.value?.id || memberId.value
);
const activeTab = ref('');
const isEditing = ref(false);
const saving = ref(false);
const editForm = ref({});
const referralDepartmentPreview = ref('');
const hydratedReferralId = ref('');
const hydratedMentorId = ref('');

// 下拉選項
const rankOptions = ref([]);
const departmentOptions = ref([]);
const referralOptions = ref([]);
const recruiterOptions = ref([]);
const mentorOptions = ref([]);
const referralLoading = ref(false);
const recruiterLoading = ref(false);
const mentorLoading = ref(false);
let referralSearchTimer = null;
let recruiterSearchTimer = null;
let mentorSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250;

// 將 points 物件轉為陣列以便渲染卡片
const pointsArray = computed(() => {
    const p = memberDetail.value?.points;
    if (!p || typeof p !== 'object') return [];
    return Object.entries(p).map(([code, obj]) => ({
        code,
        total: Number(obj?.total ?? 0),
        usable: Number(obj?.usable ?? 0),
        frozen: Number(obj?.frozen ?? 0),
        cumulative_earned: Number(obj?.cumulative_earned ?? 0)
    }));
});

// 是否為夥伴（用於控制欄位禁用）
const isPartner = computed(() => editForm.value?.identity === 'partner');

// 積分紀錄狀態
const pointTypeOptions = ref([]);
const pointFilters = ref({
    point_type: undefined,
    created_from: undefined,
    created_to: undefined
});
const pointLogs = ref([]);
const pointPagination = ref({ current: 1, pageSize: 20, total: 0 });

const pointColumns = [
    // 流水號由 DataTable 的 index 欄呈現，不再重複顯示 ID
    {
        title: '積分類型',
        dataIndex: 'point_type',
        key: 'point_type',
        width: 120
    },
    {
        title: '變動數值',
        dataIndex: 'change',
        key: 'change',
        width: 120,
        align: 'right'
    },
    {
        title: '原餘額',
        dataIndex: 'before',
        key: 'before',
        width: 120,
        align: 'right'
    },
    {
        title: '新餘額',
        dataIndex: 'after',
        key: 'after',
        width: 120,
        align: 'right'
    },
    { title: '變動原因', dataIndex: 'reason', key: 'reason' },
    {
        title: '操作者',
        dataIndex: 'reviewer_name',
        key: 'reviewer_name',
        width: 120,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '建立時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 200
    }
];

// 推薦／招募／輔導紀錄狀態與欄位
const referralTypeOptions = [
    { label: '全部', value: 'all' },
    { label: '推薦人', value: 'referral' },
    { label: '招募人', value: 'recruiter' },
    { label: '輔導人', value: 'mentor' }
];
const referralFilters = ref({
    type: 'all',
    changed_from: undefined,
    changed_to: undefined
});
const referralLogs = ref([]);
const referralPagination = ref({ current: 1, pageSize: 20, total: 0 });

const referralColumns = [
    { title: '類型', dataIndex: 'type_name', key: 'type_name', width: 120 },
    {
        title: '變更前',
        dataIndex: 'from_name',
        key: 'from_name',
        width: 140,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '變更後',
        dataIndex: 'to_name',
        key: 'to_name',
        width: 140,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '變動原因',
        dataIndex: 'reason',
        key: 'reason',
        // 展開顯示完整內容，不要省略號
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '變動時間',
        dataIndex: 'changed_at',
        key: 'changed_at',
        width: 200
    }
];

// 事業部異動紀錄狀態與欄位
const departmentFilters = ref({
    changed_from: undefined,
    changed_to: undefined
});
const departmentLogs = ref([]);
const departmentPagination = ref({ current: 1, pageSize: 20, total: 0 });

const departmentColumns = [
    {
        title: '類型',
        dataIndex: 'field_name',
        key: 'field_name',
        width: 120,
        customRender: ({ text }) => text || '輔導事業部'
    },
    {
        title: '變更前',
        dataIndex: 'from_department_name',
        key: 'from_department_name',
        width: 200,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '變更後',
        dataIndex: 'to_department_name',
        key: 'to_department_name',
        width: 200,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '變動原因',
        dataIndex: 'reason',
        key: 'reason',
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '操作者',
        dataIndex: 'reviewer_name',
        key: 'reviewer_name',
        width: 120,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '變動時間',
        dataIndex: 'changed_at',
        key: 'changed_at',
        width: 200
    }
];

const fetchDepartmentLogs = async () => {
    const userId = route.params.id;
    if (!userId) return;
    try {
        const params = {
            user_id: userId,
            page: departmentPagination.value.current,
            per_page: departmentPagination.value.pageSize
        };
        if (departmentFilters.value.changed_from)
            params.changed_from = departmentFilters.value.changed_from;
        if (departmentFilters.value.changed_to)
            params.changed_to = departmentFilters.value.changed_to;

        const res = await getDepartmentLogs(params);
        const list =
            res?.data?.data?.data || res?.data?.data || res?.data || [];
        departmentLogs.value = Array.isArray(list) ? list : [];
        const total =
            res?.data?.data?.total ||
            res?.data?.total ||
            departmentLogs.value.length;
        const current =
            res?.data?.data?.current_page || departmentPagination.value.current;
        const perPage =
            res?.data?.data?.per_page || departmentPagination.value.pageSize;
        departmentPagination.value = { current, pageSize: perPage, total };
    } catch (e) {
        departmentLogs.value = [];
    }
};

const onDepartmentTableChange = ({ pagination }) => {
    departmentPagination.value.current = pagination.current;
    departmentPagination.value.pageSize = pagination.pageSize;
    fetchDepartmentLogs();
};

const onSearchDepartmentLogs = () => {
    departmentPagination.value.current = 1;
    fetchDepartmentLogs();
};

const onResetDepartmentFilters = () => {
    departmentFilters.value = {
        changed_from: undefined,
        changed_to: undefined
    };
    departmentPagination.value.current = 1;
    fetchDepartmentLogs();
};

const refreshDepartmentLogs = () => fetchDepartmentLogs();
// ====== Rank Logs ======
const rankFilters = ref({ assigned_from: undefined, assigned_to: undefined });
const rankLogs = ref([]);
const rankPagination = ref({ current: 1, pageSize: 20, total: 0 });

const rankColumns = [
    {
        title: '變更前',
        dataIndex: 'from_rank_name',
        key: 'from_rank_name',
        width: 160,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '變更後',
        dataIndex: 'to_rank_name',
        key: 'to_rank_name',
        width: 160,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '來源',
        dataIndex: 'source',
        key: 'source',
        width: 120,
        customRender: ({ text }) => {
            if (text == null || text === '') return '-';
            const key = String(text).toLowerCase();
            if (key === 'manual') return '手動';
            if (key === 'system') return '系統';
            return text;
        }
    },
    {
        title: '原因',
        dataIndex: 'reason',
        key: 'reason',
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '操作者',
        dataIndex: 'reviewer_name',
        key: 'reviewer_name',
        width: 120,
        customRender: ({ text }) => (text == null || text === '' ? '-' : text)
    },
    {
        title: '異動時間',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 200
    }
];

const fetchRankLogs = async () => {
    const userId = route.params.id;
    if (!userId) return;
    try {
        const params = {
            user_id: userId,
            page: rankPagination.value.current,
            per_page: rankPagination.value.pageSize
        };
        if (rankFilters.value.assigned_from)
            params.assigned_from = rankFilters.value.assigned_from;
        if (rankFilters.value.assigned_to)
            params.assigned_to = rankFilters.value.assigned_to;

        const res = await getRankLogs(params);
        const list =
            res?.data?.data?.data || res?.data?.data || res?.data || [];
        rankLogs.value = Array.isArray(list) ? list : [];
        const total =
            res?.data?.data?.total || res?.data?.total || rankLogs.value.length;
        const current =
            res?.data?.data?.current_page || rankPagination.value.current;
        const perPage =
            res?.data?.data?.per_page || rankPagination.value.pageSize;
        rankPagination.value = { current, pageSize: perPage, total };
    } catch (e) {
        rankLogs.value = [];
    }
};

const onRankTableChange = ({ pagination }) => {
    rankPagination.value.current = pagination.current;
    rankPagination.value.pageSize = pagination.pageSize;
    fetchRankLogs();
};

const onSearchRankLogs = () => {
    rankPagination.value.current = 1;
    fetchRankLogs();
};

const onResetRankFilters = () => {
    rankFilters.value = { assigned_from: undefined, assigned_to: undefined };
    rankPagination.value.current = 1;
    fetchRankLogs();
};

const refreshRankLogs = () => fetchRankLogs();

// ====== Task Records ======
// 視圖模式：list 或 tree
const taskViewMode = ref('list');

// 篩選相關
const taskFilterCollapseActiveKey = ref(['filters']);
const taskFilters = ref({
    status: undefined
});
const taskNodePath = ref({
    missionId: undefined,
    chapterId: undefined,
    taskId: undefined,
    stepId: undefined
});
const taskDateRange = ref(null);

// 下拉選單選項
const taskMissionOptions = ref([]);
const taskChapterOptions = ref([]);
const taskTaskOptions = ref([]);
const taskStepOptions = ref([]);
const taskLoadingMissions = ref(false);
const taskLoadingChapters = ref(false);
const taskLoadingTasks = ref(false);
const taskLoadingSteps = ref(false);

// 使用 computed 轉換選項格式
const taskMissionSelectOptions = computed(() =>
    taskMissionOptions.value.map((mission) => ({
        label: mission.name || `未命名主線（ID: ${mission.node_id}）`,
        value: mission.node_id
    }))
);

const taskChapterSelectOptions = computed(() =>
    taskChapterOptions.value.map((chapter) => ({
        label: chapter.name || `未命名章節（ID: ${chapter.node_id}）`,
        value: chapter.node_id
    }))
);

const taskTaskSelectOptions = computed(() =>
    taskTaskOptions.value.map((task) => ({
        label: task.name || `未命名任務（ID: ${task.node_id}）`,
        value: task.node_id
    }))
);

const taskStepSelectOptions = computed(() =>
    taskStepOptions.value.map((step) => ({
        label: step.name || `未命名步驟（ID: ${step.node_id}）`,
        value: step.node_id
    }))
);

// 狀態選項
const taskStatusOptions = [
    { label: '進行中', value: 'in_progress' },
    { label: '已完成', value: 'completed' },
    { label: '失敗', value: 'failed' },
    { label: '已取消', value: 'cancelled' }
];

// 記錄列表相關
const taskProgressList = ref([]);
const taskProgressPagination = ref({
    total: 0,
    current: 1,
    pageSize: 20
});
const taskLoading = ref(false);

// 樹狀統計相關
const taskTreeStatsData = ref([]);
// 追蹤哪些主線是展開的（預設全部收合）
const taskExpandedMissions = ref(new Set());

// 節點執行者 Modal
const taskNodeUsersModalVisible = ref(false);
const taskNodeUsersModalTitle = ref('');
const taskCurrentNodeId = ref(null);
const taskCurrentNodeUsersData = ref(null);
const taskNodeUsersList = ref([]);
const taskNodeUsersPagination = ref({
    total: 0,
    current: 1,
    pageSize: 20
});
const taskNodeUsersLoading = ref(false);

// 表格欄位定義
const taskProgressColumns = [
    {
        title: '節點',
        key: 'node',
        width: 200,
        fixed: 'left'
    },
    {
        title: '階層路徑',
        key: 'hierarchy',
        width: 300
    },
    {
        title: '狀態',
        key: 'status',
        width: 120
    },
    {
        title: '條件摘要',
        key: 'conditions_summary',
        width: 180
    },
    {
        title: '開始時間',
        key: 'started_at',
        width: 180
    },
    {
        title: '完成時間',
        key: 'completed_at',
        width: 180
    }
];

// 計算啟用的篩選數量
const getTaskActiveFilterCount = () => {
    let count = 0;
    if (taskFilters.value.status) count++;
    if (taskNodePath.value.missionId) count++;
    if (taskNodePath.value.chapterId) count++;
    if (taskNodePath.value.taskId) count++;
    if (taskNodePath.value.stepId) count++;
    if (taskDateRange.value && taskDateRange.value.length === 2) count++;
    return count;
};

// 載入主線選單
const loadTaskMissions = async () => {
    taskLoadingMissions.value = true;
    try {
        const response = await getMissionDropdown();
        if (response?.result) {
            taskMissionOptions.value = response.data || [];
        } else {
            taskMissionOptions.value = [];
        }
    } catch (error) {
        console.error('載入主線選單失敗：', error);
        taskMissionOptions.value = [];
    } finally {
        taskLoadingMissions.value = false;
    }
};

// 載入章節選單
const loadTaskChapters = async (missionId) => {
    if (!missionId) {
        taskChapterOptions.value = [];
        return;
    }
    taskLoadingChapters.value = true;
    try {
        const response = await getChapterDropdown({
            mission_id: missionId
        });
        if (response?.result) {
            taskChapterOptions.value = response.data || [];
        } else {
            taskChapterOptions.value = [];
        }
    } catch (error) {
        console.error('載入章節選單失敗：', error);
        taskChapterOptions.value = [];
    } finally {
        taskLoadingChapters.value = false;
    }
};

// 載入任務選單
const loadTaskTasks = async (chapterId, missionId) => {
    if (!chapterId && !missionId) {
        taskTaskOptions.value = [];
        return;
    }
    taskLoadingTasks.value = true;
    try {
        const params = {};
        if (missionId) params.mission_id = missionId;
        if (chapterId) params.chapter_id = chapterId;

        const response = await getTaskDropdown(params);
        if (response?.result) {
            taskTaskOptions.value = response.data || [];
        } else {
            taskTaskOptions.value = [];
        }
    } catch (error) {
        console.error('載入任務選單失敗：', error);
        taskTaskOptions.value = [];
    } finally {
        taskLoadingTasks.value = false;
    }
};

// 載入步驟選單
const loadTaskSteps = async (taskId, chapterId, missionId) => {
    if (!taskId && !chapterId && !missionId) {
        taskStepOptions.value = [];
        return;
    }
    taskLoadingSteps.value = true;
    try {
        const params = {};
        if (missionId) params.mission_id = missionId;
        if (chapterId) params.chapter_id = chapterId;
        if (taskId) params.task_id = taskId;

        const response = await getStepDropdown(params);
        if (response?.result) {
            taskStepOptions.value = response.data || [];
        } else {
            taskStepOptions.value = [];
        }
    } catch (error) {
        console.error('載入步驟選單失敗：', error);
        taskStepOptions.value = [];
    } finally {
        taskLoadingSteps.value = false;
    }
};

// 節點路徑變更處理
const handleTaskMissionChange = async (missionId) => {
    taskNodePath.value.missionId = missionId;
    taskNodePath.value.chapterId = undefined;
    taskNodePath.value.taskId = undefined;
    taskNodePath.value.stepId = undefined;

    taskChapterOptions.value = [];
    taskTaskOptions.value = [];
    taskStepOptions.value = [];

    updateTaskNodeFilters();

    if (missionId) {
        await loadTaskChapters(missionId);
    }
};

const handleTaskChapterChange = async (chapterId) => {
    taskNodePath.value.chapterId = chapterId;
    taskNodePath.value.taskId = undefined;
    taskNodePath.value.stepId = undefined;

    taskTaskOptions.value = [];
    taskStepOptions.value = [];

    updateTaskNodeFilters();

    if (chapterId) {
        await loadTaskTasks(chapterId, taskNodePath.value.missionId);
    }
};

const handleTaskTaskChange = async (taskId) => {
    taskNodePath.value.taskId = taskId;
    taskNodePath.value.stepId = undefined;

    taskStepOptions.value = [];

    updateTaskNodeFilters();

    if (taskId) {
        await loadTaskSteps(
            taskId,
            taskNodePath.value.chapterId,
            taskNodePath.value.missionId
        );
    }
};

const handleTaskStepChange = (stepId) => {
    taskNodePath.value.stepId = stepId;
    updateTaskNodeFilters();
};

// 更新節點篩選條件
const updateTaskNodeFilters = () => {
    taskFilters.value.mission_id = undefined;
    taskFilters.value.chapter_id = undefined;
    taskFilters.value.task_id = undefined;
    taskFilters.value.step_id = undefined;

    if (taskNodePath.value.stepId) {
        taskFilters.value.step_id = taskNodePath.value.stepId;
    } else if (taskNodePath.value.taskId) {
        taskFilters.value.task_id = taskNodePath.value.taskId;
    } else if (taskNodePath.value.chapterId) {
        taskFilters.value.chapter_id = taskNodePath.value.chapterId;
    } else if (taskNodePath.value.missionId) {
        taskFilters.value.mission_id = taskNodePath.value.missionId;
    }

    handleTaskFilterChange();
};

const handleTaskNodePathClear = () => {
    taskNodePath.value = {
        missionId: undefined,
        chapterId: undefined,
        taskId: undefined,
        stepId: undefined
    };
    taskChapterOptions.value = [];
    taskTaskOptions.value = [];
    taskStepOptions.value = [];
    updateTaskNodeFilters();
};

// 日期範圍變更
const handleTaskDateRangeChange = () => {
    handleTaskFilterChange();
};

// 篩選變更處理
const handleTaskFilterChange = () => {
    if (taskViewMode.value === 'list') {
        loadTaskProgressList();
    } else {
        loadTaskTreeStats();
    }
};

// 載入進度記錄列表
const loadTaskProgressList = async () => {
    const userId = route.params.id;
    if (!userId) return;

    taskLoading.value = true;
    try {
        const params = {
            user_id: userId, // 固定為當前會員
            page: taskProgressPagination.value.current,
            per_page: taskProgressPagination.value.pageSize
        };

        // 基本篩選
        if (taskFilters.value.status) {
            params.status = taskFilters.value.status;
        }

        // 節點路徑篩選
        if (taskFilters.value.step_id) {
            params.step_id = taskFilters.value.step_id;
        } else if (taskFilters.value.task_id) {
            params.task_id = taskFilters.value.task_id;
        } else if (taskFilters.value.chapter_id) {
            params.chapter_id = taskFilters.value.chapter_id;
        } else if (taskFilters.value.mission_id) {
            params.mission_id = taskFilters.value.mission_id;
        }

        // 日期範圍
        if (taskDateRange.value && taskDateRange.value.length === 2) {
            params.start_date = taskDateRange.value[0].format('YYYY-MM-DD');
            params.end_date = taskDateRange.value[1].format('YYYY-MM-DD');
        }

        const response = await getMissionProgress(params);
        if (response.result && response.data) {
            taskProgressList.value = response.data.data || [];
            if (response.data.pagination) {
                taskProgressPagination.value = {
                    total: response.data.pagination.total || 0,
                    current: response.data.pagination.current_page || 1,
                    pageSize: response.data.pagination.per_page || 20
                };
            }
        }
    } catch (error) {
        console.error('載入進度記錄失敗：', error);
        showErrorMessage('載入進度記錄失敗');
    } finally {
        taskLoading.value = false;
    }
};

// 載入樹狀統計
const loadTaskTreeStats = async () => {
    const userId = route.params.id;
    if (!userId) return;

    taskLoading.value = true;
    try {
        const params = {
            user_id: userId // 固定為當前會員
        };

        // 基本篩選
        if (taskFilters.value.status) {
            params.status = taskFilters.value.status;
        }

        // 節點路徑篩選
        if (taskFilters.value.mission_id) {
            params.mission_id = taskFilters.value.mission_id;
        }

        // 日期範圍
        if (taskDateRange.value && taskDateRange.value.length === 2) {
            params.start_date = taskDateRange.value[0].format('YYYY-MM-DD');
            params.end_date = taskDateRange.value[1].format('YYYY-MM-DD');
        }

        const response = await getMissionTreeStats(params);
        if (response.result && response.data) {
            taskTreeStatsData.value = response.data.missions || [];
        }
    } catch (error) {
        console.error('載入樹狀統計失敗：', error);
        showErrorMessage('載入樹狀統計失敗');
    } finally {
        taskLoading.value = false;
    }
};

// 表格變更處理
const handleTaskProgressTableChange = ({ pagination }) => {
    if (pagination) {
        taskProgressPagination.value.current = pagination.current;
        taskProgressPagination.value.pageSize = pagination.pageSize;
    }
    loadTaskProgressList();
};

// 統一載入資料
const loadTaskData = () => {
    if (taskViewMode.value === 'list') {
        loadTaskProgressList();
    } else {
        loadTaskTreeStats();
    }
};

// 狀態標籤和顏色
const getTaskStatusLabel = (status) => {
    const statusMap = {
        in_progress: '進行中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消',
        pending: '待處理',
        not_started: '未開始'
    };
    return statusMap[status] || status || '-';
};

const getTaskStatusColor = (status) => {
    const colorMap = {
        in_progress: 'processing',
        completed: 'success',
        failed: 'error',
        cancelled: 'default',
        pending: 'warning',
        not_started: 'default'
    };
    return colorMap[status] || 'default';
};

// 節點類型標籤和顏色
const getTaskNodeTypeLabel = (nodeType) => {
    const typeMap = {
        mission: '主線',
        chapter: '章節',
        task: '任務',
        step: '步驟'
    };
    return typeMap[nodeType] || nodeType || '-';
};

const getTaskNodeTypeColor = (nodeType) => {
    const colorMap = {
        mission: 'purple',
        chapter: 'cyan',
        task: 'blue',
        step: 'green'
    };
    return colorMap[nodeType] || 'default';
};

// 格式化日期時間
const formatTaskDateTime = (dateTime) => {
    return formatDateTime(dateTime);
};

// 獲取節點進度狀態（從 stats 中獲取該會員的狀態）
const getNodeProgressStatus = (nodeData) => {
    // 在個人視圖中，stats 應該包含該會員的進度資訊
    // 如果 stats 中有 status 欄位，直接返回
    if (nodeData?.stats?.status) {
        return nodeData.stats.status;
    }
    // 如果 total_progress 為 0，表示未開始（僅在 mission 層級顯示）
    if (nodeData?.stats?.total_progress === 0) {
        // 只在 mission 層級返回 not_started
        if (nodeData?.node?.node_type === 'mission') {
            return 'not_started';
        }
        // 其他層級返回 null，不顯示狀態
        return null;
    }
    // 如果有 completed 且 > 0，表示已完成
    if (nodeData?.stats?.completed > 0) {
        return 'completed';
    }
    // 如果有 in_progress 且 > 0，表示進行中
    if (nodeData?.stats?.in_progress > 0) {
        return 'in_progress';
    }
    // 如果有 failed 且 > 0，表示失敗
    if (nodeData?.stats?.failed > 0) {
        return 'failed';
    }
    // 如果有 cancelled 且 > 0，表示已取消
    if (nodeData?.stats?.cancelled > 0) {
        return 'cancelled';
    }
    // 如果有 total_progress 且 > 0，表示有進度
    if (nodeData?.stats?.total_progress > 0) {
        return 'in_progress';
    }
    return null;
};

// 獲取節點進度時間（顯示完成時間或開始時間）
const getNodeProgressTime = (nodeData) => {
    if (nodeData?.stats?.completed_at) {
        return `完成：${formatTaskDateTime(nodeData.stats.completed_at)}`;
    }
    if (nodeData?.stats?.started_at) {
        return `開始：${formatTaskDateTime(nodeData.stats.started_at)}`;
    }
    return null;
};

// 計算進度持續時間
const getProgressDuration = (startedAt, completedAt) => {
    if (!startedAt) return '-';
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diff = end - start;

    if (diff < 0) return '-';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
        return `${days} 天 ${hours} 小時 ${minutes} 分鐘`;
    } else if (hours > 0) {
        return `${hours} 小時 ${minutes} 分鐘`;
    } else {
        return `${minutes} 分鐘`;
    }
};

// 節點詳細歷程 Modal
const taskNodeDetailModalVisible = ref(false);
const taskNodeDetailModalTitle = ref('');
const taskNodeDetailData = ref(null);
const taskNodeDetailProgressList = ref([]); // 進度記錄列表（基本資訊）
const taskNodeDetailLoading = ref(false);
const taskNodeDetailDetails = ref(null); // 選中的進度記錄詳細資訊
const taskNodeDetailDetailsLoading = ref(false);
const selectedProgressId = ref(null); // 當前選中的進度記錄 ID
const taskDetailActiveTab = ref('progress'); // 詳細資訊 Tab 的 active key

// 點擊節點查看詳細歷程
const handleNodeClick = async (node, nodeData) => {
    // 未開始的節點不顯示詳細歷程
    const status = getNodeProgressStatus(nodeData);
    if (!status || status === 'not_started') {
        return;
    }

    const userId = route.params.id;
    if (!userId || !node?.id) return;

    taskNodeDetailModalVisible.value = true;
    taskNodeDetailModalTitle.value = `${node.name || '節點'} - 詳細歷程`;
    taskNodeDetailData.value = { node };
    taskNodeDetailProgressList.value = [];
    taskNodeDetailDetails.value = null;
    selectedProgressId.value = null;
    taskDetailActiveTab.value = 'progress';
    taskNodeDetailLoading.value = true;

    try {
        // 使用新的列表 API 獲取基本資訊
        const params = {
            node_id: node.id,
            per_page: 50 // 獲取更多記錄
        };

        const response = await getUserMissionProgress(userId, params);
        if (response.result && response.data) {
            taskNodeDetailProgressList.value = response.data.data || [];
            // 如果有記錄，自動載入第一筆的詳細資訊
            if (taskNodeDetailProgressList.value.length > 0) {
                handleProgressItemClick(taskNodeDetailProgressList.value[0].id);
            }
        }
    } catch (error) {
        console.error('載入節點進度記錄失敗：', error);
        showErrorMessage('載入節點進度記錄失敗');
    } finally {
        taskNodeDetailLoading.value = false;
    }
};

// 點擊進度記錄項目查看詳細資訊
const handleProgressItemClick = async (progressId) => {
    const userId = route.params.id;
    if (!userId || !progressId) return;

    selectedProgressId.value = progressId;
    taskNodeDetailDetails.value = null;
    taskNodeDetailDetailsLoading.value = true;

    try {
        const response = await getUserMissionProgressDetails(
            userId,
            progressId
        );
        if (response.result && response.data) {
            taskNodeDetailDetails.value = response.data;
        }
    } catch (error) {
        console.error('載入進度詳細資訊失敗：', error);
        showErrorMessage('載入進度詳細資訊失敗');
    } finally {
        taskNodeDetailDetailsLoading.value = false;
    }
};

// 關閉節點詳細歷程 Modal
const handleTaskNodeDetailModalCancel = () => {
    taskNodeDetailModalVisible.value = false;
    taskNodeDetailData.value = null;
    taskNodeDetailProgressList.value = [];
    taskNodeDetailDetails.value = null;
    selectedProgressId.value = null;
};

// 點擊進度記錄行查看詳細歷程
const handleProgressRecordClick = async (record) => {
    if (!record?.node?.id) return;

    const userId = route.params.id;
    if (!userId) return;

    taskNodeDetailModalVisible.value = true;
    taskNodeDetailModalTitle.value = `${record.node?.name || '節點'} - 詳細歷程`;
    taskNodeDetailData.value = { node: record.node };
    taskNodeDetailProgressList.value = [];
    taskNodeDetailDetails.value = null;
    selectedProgressId.value = null;
    taskDetailActiveTab.value = 'progress';
    taskNodeDetailLoading.value = true;

    try {
        const params = {
            node_id: record.node.id,
            per_page: 50
        };

        const response = await getUserMissionProgress(userId, params);
        if (response.result && response.data) {
            taskNodeDetailProgressList.value = response.data.data || [];
            // 如果有記錄，自動載入第一筆的詳細資訊
            if (taskNodeDetailProgressList.value.length > 0) {
                handleProgressItemClick(taskNodeDetailProgressList.value[0].id);
            }
        }
    } catch (error) {
        console.error('載入節點進度記錄失敗：', error);
        showErrorMessage('載入節點進度記錄失敗');
    } finally {
        taskNodeDetailLoading.value = false;
    }
};

// 點擊階層路徑節點，自動設置 filter 並搜尋
const handleHierarchyNodeClick = async (
    nodeType,
    nodeId,
    missionId,
    chapterId,
    taskId,
    nodeName
) => {
    // 根據節點類型設置對應的 filter
    if (nodeType === 'mission') {
        taskNodePath.value = {
            missionId: nodeId,
            chapterId: undefined,
            taskId: undefined,
            stepId: undefined
        };
        // 確保選項中包含當前節點
        const missionExists = taskMissionOptions.value.some(
            (m) => m.node_id === nodeId
        );
        if (!missionExists && nodeName) {
            taskMissionOptions.value.push({
                node_id: nodeId,
                name: nodeName
            });
        }
        await loadTaskChapters(nodeId);
    } else if (nodeType === 'chapter') {
        taskNodePath.value = {
            missionId: missionId,
            chapterId: nodeId,
            taskId: undefined,
            stepId: undefined
        };
        // 確保選項中包含當前節點
        const chapterExists = taskChapterOptions.value.some(
            (c) => c.node_id === nodeId
        );
        if (!chapterExists && nodeName) {
            taskChapterOptions.value.push({
                node_id: nodeId,
                name: nodeName
            });
        }
        await loadTaskTasks(nodeId, missionId);
    } else if (nodeType === 'task') {
        taskNodePath.value = {
            missionId: missionId,
            chapterId: chapterId,
            taskId: nodeId,
            stepId: undefined
        };
        // 確保選項中包含當前節點
        const taskExists = taskTaskOptions.value.some(
            (t) => t.node_id === nodeId
        );
        if (!taskExists && nodeName) {
            taskTaskOptions.value.push({
                node_id: nodeId,
                name: nodeName
            });
        }
        await loadTaskSteps(nodeId, chapterId, missionId);
    } else if (nodeType === 'step') {
        taskNodePath.value = {
            missionId: missionId,
            chapterId: chapterId,
            taskId: taskId,
            stepId: nodeId
        };
        // 確保選項中包含當前節點
        const stepExists = taskStepOptions.value.some(
            (s) => s.node_id === nodeId
        );
        if (!stepExists && nodeName) {
            taskStepOptions.value.push({
                node_id: nodeId,
                name: nodeName
            });
        }
    }

    // 展開篩選面板
    taskFilterCollapseActiveKey.value = ['filters'];

    // 觸發搜尋
    updateTaskNodeFilters();
    loadTaskProgressList();
};

// 展開/收合主線
const toggleMissionExpand = (missionId) => {
    if (taskExpandedMissions.value.has(missionId)) {
        taskExpandedMissions.value.delete(missionId);
    } else {
        taskExpandedMissions.value.add(missionId);
    }
};

// 檢查主線是否展開
const isMissionExpanded = (missionId) => {
    return taskExpandedMissions.value.has(missionId);
};

// 監聽視圖模式切換
watch(taskViewMode, (newMode) => {
    loadTaskData();
});

// 跳轉到審核列表（從列表視圖）
const handleGoToTaskReviews = (record) => {
    const query = {
        result_status: 'pending'
    };

    // 添加用戶名稱過濾（而不是 user_id）
    if (memberDetail.value?.name) {
        query.user_name = memberDetail.value.name;
    }

    // 添加任務節點篩選條件
    if (record.node?.hierarchy) {
        const hierarchy = record.node.hierarchy;
        if (hierarchy.step?.node_id) {
            query.step_id = hierarchy.step.node_id;
        } else if (hierarchy.task?.node_id) {
            query.task_id = hierarchy.task.node_id;
        } else if (hierarchy.chapter?.node_id) {
            query.chapter_id = hierarchy.chapter.node_id;
        } else if (hierarchy.mission?.node_id) {
            query.mission_id = hierarchy.mission.node_id;
        }
    }

    router.push({
        path: '/missions/mission-reviews',
        query
    });
};

// 跳轉到審核列表（從樹狀統計視圖）
const handleGoToTaskReviewsFromProgress = (progress) => {
    const query = {
        result_status: 'pending'
    };

    // 添加用戶名稱過濾（而不是 user_id）
    if (memberDetail.value?.name) {
        query.user_name = memberDetail.value.name;
    }

    // 添加任務節點篩選條件
    if (progress.node?.hierarchy) {
        const hierarchy = progress.node.hierarchy;
        if (hierarchy.step?.node_id) {
            query.step_id = hierarchy.step.node_id;
        } else if (hierarchy.task?.node_id) {
            query.task_id = hierarchy.task.node_id;
        } else if (hierarchy.chapter?.node_id) {
            query.chapter_id = hierarchy.chapter.node_id;
        } else if (hierarchy.mission?.node_id) {
            query.mission_id = hierarchy.mission.node_id;
        }
    }

    router.push({
        path: '/missions/mission-reviews',
        query
    });
};

// 監聽 activeTab 變化，當切換到任務紀錄時載入資料
watch(
    () => activeTab.value,
    (newTab) => {
        if (newTab === 'task') {
            loadTaskMissions();
            loadTaskData();
        }
        if (newTab === 'system_access') {
            loadSystemAccess();
        }
    }
);
const fetchReferralLogs = async () => {
    const userId = route.params.id;
    if (!userId) return;
    try {
        const params = {
            user_id: userId,
            page: referralPagination.value.current,
            per_page: referralPagination.value.pageSize,
            type: referralFilters.value.type || 'all'
        };
        if (referralFilters.value.changed_from)
            params.changed_from = referralFilters.value.changed_from;
        if (referralFilters.value.changed_to)
            params.changed_to = referralFilters.value.changed_to;

        const res = await getReferralLogs(params);
        const list =
            res?.data?.data?.data || res?.data?.data || res?.data || [];
        referralLogs.value = Array.isArray(list) ? list : [];
        const total =
            res?.data?.data?.total ||
            res?.data?.total ||
            referralLogs.value.length;
        const current =
            res?.data?.data?.current_page || referralPagination.value.current;
        const perPage =
            res?.data?.data?.per_page || referralPagination.value.pageSize;
        referralPagination.value = { current, pageSize: perPage, total };
    } catch (e) {
        referralLogs.value = [];
    }
};

const onReferralTableChange = ({ pagination }) => {
    referralPagination.value.current = pagination.current;
    referralPagination.value.pageSize = pagination.pageSize;
    fetchReferralLogs();
};

const onSearchReferralLogs = () => {
    referralPagination.value.current = 1;
    fetchReferralLogs();
};

const onResetReferralFilters = () => {
    referralFilters.value = {
        type: 'all',
        changed_from: undefined,
        changed_to: undefined
    };
    referralPagination.value.current = 1;
    fetchReferralLogs();
};

const refreshReferralLogs = () => fetchReferralLogs();

const fetchPointTypeOptions = async () => {
    try {
        const res = await getPointTypeOptions();
        const raw = res?.data?.data || res?.data || {};
        // API 回傳為物件：{ CP: "個人貢獻度積分", XP: "團隊貢獻度積分" }
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            pointTypeOptions.value = Object.keys(raw).map((key) => ({
                label: key, // 只顯示 key
                value: key
            }));
        } else if (Array.isArray(raw)) {
            // 容錯：若是陣列，仍只顯示可推導出的 code/name
            pointTypeOptions.value = raw.map((x) => ({
                label: x.code || x.name || String(x.id),
                value: x.code || x.name || String(x.id)
            }));
        } else {
            pointTypeOptions.value = [];
        }
    } catch (e) {
        pointTypeOptions.value = [];
    }
};

const fetchPointLogs = async () => {
    const userId = route.params.id;
    if (!userId) return;
    try {
        const params = {
            user_id: userId,
            page: pointPagination.value.current,
            per_page: pointPagination.value.pageSize
        };
        if (pointFilters.value.point_type)
            params.point_type = pointFilters.value.point_type;
        if (pointFilters.value.created_from)
            params.created_from = pointFilters.value.created_from;
        if (pointFilters.value.created_to)
            params.created_to = pointFilters.value.created_to;

        const res = await getPointLogs(params);
        const arr = Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
              ? res.data
              : [];
        pointLogs.value = arr;
        const total =
            res?.data?.pagination?.total ||
            res?.data?.total ||
            res?.total ||
            arr.length;
        const current =
            res?.data?.pagination?.current_page ||
            pointPagination.value.current;
        const perPage =
            res?.data?.pagination?.per_page || pointPagination.value.pageSize;
        pointPagination.value = { current, pageSize: perPage, total };
    } catch (e) {
        pointLogs.value = [];
    }
};

const onPointTableChange = ({ pagination }) => {
    pointPagination.value.current = pagination.current;
    pointPagination.value.pageSize = pagination.pageSize;
    fetchPointLogs();
};

const onSearchPointLogs = () => {
    pointPagination.value.current = 1;
    fetchPointLogs();
};

const onResetPointFilters = () => {
    pointFilters.value = {
        point_type: undefined,
        created_from: undefined,
        created_to: undefined
    };
    pointPagination.value.current = 1;
    fetchPointLogs();
};

const refreshPointLogs = () => fetchPointLogs();

// 等級紀錄狀態
const levelFilters = ref({
    upgraded_from: undefined,
    upgraded_to: undefined
});
const levelLogs = ref([]);
const levelPagination = ref({ current: 1, pageSize: 20, total: 0 });

const levelColumns = [
    {
        title: '等級變動',
        key: 'level_change',
        width: 150,
        slots: { customRender: 'level_change' }
    },
    {
        title: '觸發來源',
        dataIndex: 'trigger_source',
        key: 'trigger_source',
        width: 120
    },
    {
        title: '身份',
        key: 'role',
        width: 100,
        slots: { customRender: 'role' }
    },
    {
        title: '聘階',
        key: 'rank',
        width: 120,
        slots: { customRender: 'rank' }
    },
    {
        title: '狀態',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        slots: { customRender: 'status' }
    },
    {
        title: '原因',
        dataIndex: 'reason',
        key: 'reason',
        width: 150
    },
    {
        title: '升級時間',
        dataIndex: 'upgraded_at',
        key: 'upgraded_at',
        width: 180,
        slots: { customRender: 'upgraded_at' }
    }
];

const fetchLevelLogs = async () => {
    const userId = route.params.id;
    if (!userId) return;
    try {
        const params = {
            user_id: userId,
            page: levelPagination.value.current,
            per_page: levelPagination.value.pageSize
        };
        if (levelFilters.value.upgraded_from)
            params.upgraded_from = levelFilters.value.upgraded_from;
        if (levelFilters.value.upgraded_to)
            params.upgraded_to = levelFilters.value.upgraded_to;

        const res = await getLevelLogs(params);
        if (res?.result || res?.success) {
            const list = res.data?.data || res.data?.list || [];
            const paginationData = res.data?.pagination || {};
            levelLogs.value = list;
            levelPagination.value = {
                current: paginationData.current_page || 1,
                pageSize:
                    paginationData.per_page || levelPagination.value.pageSize,
                total: paginationData.total || 0
            };
        } else {
            levelLogs.value = [];
            levelPagination.value.total = 0;
        }
    } catch (e) {
        console.error('取得等級紀錄失敗', e);
        levelLogs.value = [];
        levelPagination.value.total = 0;
    }
};

const onLevelTableChange = ({ pagination }) => {
    levelPagination.value.current = pagination.current;
    levelPagination.value.pageSize = pagination.pageSize;
    fetchLevelLogs();
};

const onSearchLevelLogs = () => {
    levelPagination.value.current = 1;
    fetchLevelLogs();
};

const onResetLevelFilters = () => {
    levelFilters.value = {
        upgraded_from: undefined,
        upgraded_to: undefined
    };
    levelPagination.value.current = 1;
    fetchLevelLogs();
};

const refreshLevelLogs = () => fetchLevelLogs();

// 組織圖（夥伴）
const orgModalVisible = ref(false);
const orgChartLoading = ref(false);
const orgChartData = ref([]);
const orgLineType = ref('referral');
const orgChartKey = ref(0);

const openOrgChart = async () => {
    orgModalVisible.value = true;
    await fetchOrgChart();
};

const onOrgLineTypeChange = async (val) => {
    orgLineType.value = val;
    await fetchOrgChart();
};

// 當彈窗開啟後重新掛載一次圖表，避免初始尺寸為 0 導致連線錯位
watch(
    () => orgModalVisible.value,
    async (visible) => {
        if (visible) {
            await nextTick();
            setTimeout(() => {
                orgChartKey.value += 1;
            }, 0);
        }
    }
);

const fetchOrgChart = async () => {
    const userId = route.params.id;
    if (!userId) return;
    try {
        orgChartLoading.value = true;
        const res = await getUserOrganizationChart(userId, {
            type: orgLineType.value
        });
        // 後端格式：{ data: { tree: [...] } }，確保抓到 tree
        let tree =
            res?.data?.data?.tree ||
            res?.data?.tree ||
            res?.tree ||
            res?.data?.data ||
            res?.data ||
            [];
        if (!Array.isArray(tree)) tree = [tree];

        const transformNode = (n) => ({
            name: n?.name || '-',
            rank_name: n?.rank_name || n?.rank?.name || '',
            rank_color:
                n?.rank_color || n?.rank?.color || n?.rank?.rank_color || '',
            referral_name: n?.referral_name || '',
            mentor_name: n?.mentor_name || '',
            assigned_at: n?.assigned_at || '',
            students_count: n?.students_count,
            raw: n,
            children: Array.isArray(n?.children)
                ? n.children.map((c) => transformNode(c))
                : []
        });
        orgChartData.value = tree.map((root) => transformNode(root));
    } catch (e) {
        orgChartData.value = [];
        showErrorMessage('載入組織圖失敗');
    } finally {
        orgChartLoading.value = false;
    }
};

// 確保目前值存在於下拉選項，避免進編輯時未顯示已選資料
const ensureOption = (optionsRef, value, label) => {
    if (value === undefined || value === null) return;
    const valStr = String(value);
    const exists = (optionsRef.value || []).some(
        (o) => String(o.value) === valStr
    );
    if (!exists) {
        optionsRef.value = [
            { label: label || `#${valStr}`, value: valStr },
            ...(optionsRef.value || [])
        ];
    }
};

// 權限檢查
const canViewDetail = computed(() =>
    canAccessMemberDetail(adminStore.permissions)
); // 會員詳情（含子分頁碼）
const canEdit = computed(() => adminStore.hasPermissionCode('100102')); // 修改會員（更改資料/密碼）
const canViewPointsRecord = computed(() =>
    adminStore.hasPermissionCode('100111')
); // 積分紀錄
const canViewLevelRecord = computed(() =>
    adminStore.hasPermissionCode('100112')
); // 等級紀錄
const canViewTaskRecord = computed(() =>
    adminStore.hasPermissionCode('100113')
); // 任務紀錄
const canViewReferralRecord = computed(() =>
    adminStore.hasPermissionCode('100114')
); // 推薦/輔導紀錄
const canViewDepartmentRecord = computed(() =>
    adminStore.hasPermissionCode('100115')
); // 事業部紀錄
const canViewRankRecord = computed(() =>
    adminStore.hasPermissionCode('100116')
); // 聘階紀錄
const canManageSystemAccess = computed(() =>
    adminStore.hasPermissionCode('100117')
); // 可用系統
const canViewPartnerOrg = computed(() =>
    adminStore.hasPermissionCode('100118')
);
const canEditPartnerOrg = computed(
    () =>
        canEdit.value && adminStore.hasPermissionCode('100119')
);
/** 除籍：需「修改夥伴組織」；僅現任夥伴顯示 */
const canDeregisterPartner = computed(
    () =>
        canEditPartnerOrg.value &&
        memberDetail.value?.identity === 'partner'
);
/** 帳號黑名單：需「修改會員」 */
const canToggleBlacklist = computed(() => canEdit.value);
const isAccountBlacklisted = computed(
    () => memberDetail.value && memberDetail.value.status === false
);
const isGeneralAdminDetail = computed(
    () => detailProfile.value === 'generalAdmin'
);
/** 用戶管理詳情：沒有改組織權限時，仍可只改推薦人 */
const canEditReferralOnly = computed(
    () => isGeneralAdminDetail.value && !canEditPartnerOrg.value
);
const canViewActivityHistory = computed(() =>
    adminStore.hasPermissionCode('100120')
);

const detailProfile = computed(() =>
    getDetailProfileFromListRoute(route.query.from)
);

const canViewSensitivePii = computed(() =>
    canViewSensitivePersonalFields(detailProfile.value)
);

const allowedDetailTabs = computed(
    () => MEMBER_DETAIL_TABS[detailProfile.value]
);

const tabAllowed = (key) => {
    const allowed = allowedDetailTabs.value;
    return !allowed || allowed.includes(key);
};

const SYSTEM_ACCESS_ZONE_MARKS = ['一', '二', '三', '四', '五'];

const SYSTEM_ACCESS_DEFINITIONS = [
    {
        code: 'balance_sheet',
        title: '資產負債表',
        roleOptions: [
            { label: '一般', value: 'member' },
            { label: '諮詢師', value: 'consultant' },
            { label: '管理', value: 'admin' },
            { label: '工程師', value: 'engineer' }
        ]
    },
    {
        code: 'booking',
        title: '預約排班',
        roleOptions: [
            { label: '一般會員', value: 'member' },
            { label: '諮詢師', value: 'provider' },
            { label: '管理', value: 'admin' },
            { label: '工程師', value: 'engineer' }
        ]
    }
];

const emptySystemAccessForm = () =>
    Object.fromEntries(
        SYSTEM_ACCESS_DEFINITIONS.map((d) => [
            d.code,
            { enabled: false, roles: ['member'] }
        ])
    );

const systemAccessRows = ref([]);
const systemAccessForm = ref(emptySystemAccessForm());
const systemAccessLoading = ref(false);
const systemAccessSaving = ref(false);
/** 避免管理／諮詢師連動 watch 互相觸發 */
const systemAccessRoleSyncing = ref(false);

const syncSystemAccessFormFromRows = () => {
    systemAccessRoleSyncing.value = true;
    const next = emptySystemAccessForm();
    for (const def of SYSTEM_ACCESS_DEFINITIONS) {
        const row = systemAccessRows.value.find(
            (r) => r.system_code === def.code && r.status === 'active'
        );
        next[def.code] = {
            enabled: !!row,
            roles:
                row?.roles && Array.isArray(row.roles) && row.roles.length
                    ? row.roles.filter((r) => r !== 'role_admin')
                    : ['member']
        };
    }
    systemAccessForm.value = next;
    nextTick(() => {
        systemAccessRoleSyncing.value = false;
    });
};

/**
 * 資產負債「管理」↔ 預約「管理」、諮詢師 ↔ provider 連動。
 * 後端 grant 升級會互加；若表單只消一邊，儲存時另一邊仍帶 admin 會再 mirror 加回。
 */
const alignLinkedSystemAccessRoles = () => {
    const bs = systemAccessForm.value.balance_sheet;
    const bk = systemAccessForm.value.booking;
    if (!bs?.enabled || !bk?.enabled) return;

    const ensureRole = (roles, role) =>
        roles.includes(role) ? roles : [...roles, role];
    const dropRole = (roles, role) => roles.filter((r) => r !== role);

    const prevBs =
        systemAccessRows.value.find(
            (r) => r.system_code === 'balance_sheet' && r.status === 'active'
        )?.roles || [];
    const prevBk =
        systemAccessRows.value.find(
            (r) => r.system_code === 'booking' && r.status === 'active'
        )?.roles || [];

    const demotedAdmin =
        (prevBs.includes('admin') && !bs.roles.includes('admin')) ||
        (prevBk.includes('admin') && !bk.roles.includes('admin'));
    if (demotedAdmin) {
        bs.roles = dropRole(bs.roles, 'admin');
        bk.roles = dropRole(bk.roles, 'admin');
    } else if (bs.roles.includes('admin') || bk.roles.includes('admin')) {
        bs.roles = ensureRole(bs.roles, 'admin');
        bk.roles = ensureRole(bk.roles, 'admin');
    }

    const demotedEngineer =
        (prevBs.includes('engineer') && !bs.roles.includes('engineer')) ||
        (prevBk.includes('engineer') && !bk.roles.includes('engineer'));
    if (demotedEngineer) {
        bs.roles = dropRole(bs.roles, 'engineer');
        bk.roles = dropRole(bk.roles, 'engineer');
    } else if (bs.roles.includes('engineer') || bk.roles.includes('engineer')) {
        bs.roles = ensureRole(bs.roles, 'engineer');
        bk.roles = ensureRole(bk.roles, 'engineer');
    }

    const demotedConsultantPair =
        (prevBs.includes('consultant') && !bs.roles.includes('consultant')) ||
        (prevBk.includes('provider') && !bk.roles.includes('provider'));
    if (demotedConsultantPair) {
        bs.roles = dropRole(bs.roles, 'consultant');
        bk.roles = dropRole(bk.roles, 'provider');
    } else if (
        bs.roles.includes('consultant') ||
        bk.roles.includes('provider')
    ) {
        bs.roles = ensureRole(bs.roles, 'consultant');
        bk.roles = ensureRole(bk.roles, 'provider');
    }
};

watch(
    () => [
        ...(systemAccessForm.value.balance_sheet?.roles || []),
        systemAccessForm.value.balance_sheet?.enabled
    ],
    (curr, prev) => {
        if (systemAccessRoleSyncing.value || systemAccessLoading.value) return;
        const roles = systemAccessForm.value.balance_sheet?.roles || [];
        const prevRoles = Array.isArray(prev)
            ? prev.filter((v) => typeof v === 'string')
            : [];
        const booking = systemAccessForm.value.booking;
        if (!booking?.enabled) return;

        systemAccessRoleSyncing.value = true;
        const hadAdmin = prevRoles.includes('admin');
        const hasAdmin = roles.includes('admin');
        if (hadAdmin && !hasAdmin) {
            booking.roles = booking.roles.filter((r) => r !== 'admin');
        } else if (!hadAdmin && hasAdmin && !booking.roles.includes('admin')) {
            booking.roles = [...booking.roles, 'admin'];
        }

        const hadEngineer = prevRoles.includes('engineer');
        const hasEngineer = roles.includes('engineer');
        if (hadEngineer && !hasEngineer) {
            booking.roles = booking.roles.filter((r) => r !== 'engineer');
        } else if (
            !hadEngineer &&
            hasEngineer &&
            !booking.roles.includes('engineer')
        ) {
            booking.roles = [...booking.roles, 'engineer'];
        }

        const hadConsultant = prevRoles.includes('consultant');
        const hasConsultant = roles.includes('consultant');
        if (hadConsultant && !hasConsultant) {
            booking.roles = booking.roles.filter((r) => r !== 'provider');
        } else if (
            !hadConsultant &&
            hasConsultant &&
            !booking.roles.includes('provider')
        ) {
            booking.roles = [...booking.roles, 'provider'];
        }
        nextTick(() => {
            systemAccessRoleSyncing.value = false;
        });
    }
);

watch(
    () => [
        ...(systemAccessForm.value.booking?.roles || []),
        systemAccessForm.value.booking?.enabled
    ],
    (curr, prev) => {
        if (systemAccessRoleSyncing.value || systemAccessLoading.value) return;
        const roles = systemAccessForm.value.booking?.roles || [];
        const prevRoles = Array.isArray(prev)
            ? prev.filter((v) => typeof v === 'string')
            : [];
        const balanceSheet = systemAccessForm.value.balance_sheet;
        if (!balanceSheet?.enabled) return;

        systemAccessRoleSyncing.value = true;
        const hadAdmin = prevRoles.includes('admin');
        const hasAdmin = roles.includes('admin');
        if (hadAdmin && !hasAdmin) {
            balanceSheet.roles = balanceSheet.roles.filter(
                (r) => r !== 'admin'
            );
        } else if (
            !hadAdmin &&
            hasAdmin &&
            !balanceSheet.roles.includes('admin')
        ) {
            balanceSheet.roles = [...balanceSheet.roles, 'admin'];
        }

        const hadEngineer = prevRoles.includes('engineer');
        const hasEngineer = roles.includes('engineer');
        if (hadEngineer && !hasEngineer) {
            balanceSheet.roles = balanceSheet.roles.filter(
                (r) => r !== 'engineer'
            );
        } else if (
            !hadEngineer &&
            hasEngineer &&
            !balanceSheet.roles.includes('engineer')
        ) {
            balanceSheet.roles = [...balanceSheet.roles, 'engineer'];
        }

        const hadProvider = prevRoles.includes('provider');
        const hasProvider = roles.includes('provider');
        if (hadProvider && !hasProvider) {
            balanceSheet.roles = balanceSheet.roles.filter(
                (r) => r !== 'consultant'
            );
        } else if (
            !hadProvider &&
            hasProvider &&
            !balanceSheet.roles.includes('consultant')
        ) {
            balanceSheet.roles = [...balanceSheet.roles, 'consultant'];
        }
        nextTick(() => {
            systemAccessRoleSyncing.value = false;
        });
    }
);

const loadSystemAccess = async () => {
    const userId = route.params.id;
    if (!userId) return;
    systemAccessLoading.value = true;
    try {
        const response = await getMemberSystemAccess(userId);
        if (response.result) {
            systemAccessRows.value = response.data?.items || [];
            syncSystemAccessFormFromRows();
        } else {
            showErrorMessage(response.message || '載入會員可用系統失敗');
        }
    } catch (error) {
        console.error(error);
        showErrorMessage('載入會員可用系統失敗');
    } finally {
        systemAccessLoading.value = false;
    }
};

const saveSystemAccess = async () => {
    if (!canEdit.value) {
        message.warning('您沒有修改會員的權限');
        return;
    }
    const userId = route.params.id;
    if (!userId) return;
    systemAccessSaving.value = true;
    try {
        alignLinkedSystemAccessRoles();
        for (const def of SYSTEM_ACCESS_DEFINITIONS) {
            const cur = systemAccessForm.value[def.code];
            const activeRow = systemAccessRows.value.find(
                (r) => r.system_code === def.code && r.status === 'active'
            );
            if (cur.enabled) {
                const roles =
                    cur.roles && cur.roles.length ? cur.roles : ['member'];
                const res = await grantMemberSystemAccess(userId, def.code, {
                    roles
                });
                if (!res.result) {
                    showErrorMessage(res.message || `「${def.title}」儲存失敗`);
                    return;
                }
            } else if (activeRow) {
                const res = await revokeMemberSystemAccess(userId, def.code, {
                    reason: '後台關閉子系統授權'
                });
                if (!res.result) {
                    showErrorMessage(res.message || `「${def.title}」關閉失敗`);
                    return;
                }
            }
        }
        message.success('已儲存');
        await loadSystemAccess();
    } catch (error) {
        console.error(error);
        showErrorMessage('儲存會員可用系統失敗');
    } finally {
        systemAccessSaving.value = false;
    }
};

// 選單項目列表（順序：基本資料、積分紀錄、推薦輔導紀錄、事業部紀錄、聘階紀錄、任務紀錄、等級紀錄）
const menuItems = computed(() => {
    const items = [];

    if (canViewDetail.value && tabAllowed('detail')) {
        items.push({ key: 'detail', label: '基本資料' });
    }

    if (canManageSystemAccess.value && tabAllowed('system_access')) {
        items.push({ key: 'system_access', label: '會員可用系統' });
    }

    if (canViewActivityHistory.value && tabAllowed('activity_history')) {
        items.push({ key: 'activity_history', label: '活動參加紀錄' });
    }

    if (canViewPointsRecord.value && tabAllowed('points')) {
        items.push({ key: 'points', label: '積分紀錄' });
    }

    if (canViewReferralRecord.value && tabAllowed('referral')) {
        items.push({ key: 'referral', label: '推薦／招募／輔導紀錄' });
    }

    if (canViewDepartmentRecord.value && tabAllowed('department')) {
        items.push({ key: 'department', label: '事業部紀錄（輔導／推薦）' });
    }

    if (canViewRankRecord.value && tabAllowed('rank')) {
        items.push({ key: 'rank', label: '聘階紀錄' });
    }

    if (canViewTaskRecord.value && tabAllowed('task')) {
        items.push({ key: 'task', label: '任務紀錄' });
    }

    if (canViewLevelRecord.value && tabAllowed('level')) {
        items.push({ key: 'level', label: '等級紀錄' });
    }

    return items;
});

// 當前選中的選單項
const currentMenuItem = computed(() => {
    return menuItems.value.find((item) => item.key === activeTab.value);
});

// 初始化選中的 tab
watch(
    menuItems,
    (items) => {
        if (items.length > 0 && !activeTab.value) {
            activeTab.value = items[0].key;
        }
    },
    { immediate: true }
);

// 身份文字轉換
const getIdentityText = (identity) => getIdentityName(identity);

const promptReason = (title, placeholder = '請輸入原因（必填）') => {
    const reason = window.prompt(`${title}\n${placeholder}`, '');
    if (reason === null) return null;
    const trimmed = String(reason).trim();
    if (!trimmed) {
        message.warning('原因為必填');
        return null;
    }
    return trimmed;
};

const openDeregisterModal = async () => {
    if (!canDeregisterPartner.value) {
        message.warning('您沒有除籍權限（需修改夥伴組織 100119）');
        return;
    }
    Modal.confirm({
        title: `確定除籍「${memberDetail.value?.name || ''}」？`,
        content: '將退回學員身分，仍可使用系統（≠黑名單）。請於下一步填寫原因。',
        okText: '繼續',
        cancelText: '取消',
        onOk: async () => {
            const reason = promptReason(
                '除籍原因',
                '必填；說明為何結束夥伴資格'
            );
            if (!reason) return Promise.reject();
            try {
                await deregisterPartner(route.params.id, reason);
                message.success('已除籍為學員');
                await loadMemberDetail();
            } catch (e) {
                showErrorMessage(
                    e?.response?.data?.message || e?.message || '除籍失敗'
                );
                return Promise.reject();
            }
        }
    });
};

const openBlacklistModal = async () => {
    if (!canToggleBlacklist.value) {
        message.warning('您沒有黑名單權限（需修改會員 100102）');
        return;
    }
    Modal.confirm({
        title: `確定將「${memberDetail.value?.name || ''}」列入黑名單？`,
        content: '列入後無法登入／使用系統。請於下一步填寫原因。',
        okText: '繼續',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
            const reason = promptReason('黑名單原因', '必填');
            if (!reason) return Promise.reject();
            try {
                await blacklistUser(route.params.id, reason);
                message.success('已列入黑名單');
                isEditing.value = false;
                await loadMemberDetail();
            } catch (e) {
                showErrorMessage(
                    e?.response?.data?.message || e?.message || '操作失敗'
                );
                return Promise.reject();
            }
        }
    });
};

const openUnblacklistModal = async () => {
    if (!canToggleBlacklist.value) {
        message.warning('您沒有黑名單權限（需修改會員 100102）');
        return;
    }
    Modal.confirm({
        title: `確定解除「${memberDetail.value?.name || ''}」黑名單？`,
        content: '請於下一步填寫解除原因。',
        okText: '繼續',
        cancelText: '取消',
        onOk: async () => {
            const reason = promptReason('解除原因', '必填');
            if (!reason) return Promise.reject();
            try {
                await unblacklistUser(route.params.id, reason);
                message.success('已解除黑名單');
                await loadMemberDetail();
            } catch (e) {
                showErrorMessage(
                    e?.response?.data?.message || e?.message || '操作失敗'
                );
                return Promise.reject();
            }
        }
    });
};

// 載入會員詳情
const loadMemberDetail = async () => {
    const userId = route.params.id;
    if (!userId) {
        showErrorMessage('缺少會員 ID');
        router.push({
            name: resolveMemberListBackRoute(route.query.from)
        });
        return;
    }

    try {
        loading.value = true;
        const response = await getUserDetail(userId);
        if (response.result) {
            memberDetail.value = response.data;
            // 補齊下拉選項的目前值，避免切換編輯才載入
            ensureOption(
                rankOptions,
                response.data.rank_id,
                response.data.rank
            );
            ensureOption(
                departmentOptions,
                response.data.department_id,
                response.data.department
            );
            ensureOption(
                departmentOptions,
                response.data.referral_department_id,
                response.data.referral_department
            );
            ensureOption(
                referralOptions,
                response.data.referral_id,
                response.data.referral
            );
            ensureOption(
                recruiterOptions,
                response.data.recruiter_id,
                response.data.recruiter
            );
            ensureOption(
                mentorOptions,
                response.data.mentor_id,
                response.data.mentor
            );
        } else {
            showErrorMessage(response.message || '載入會員資料失敗');
        }
    } catch (error) {
        console.error('載入會員詳情失敗:', error);
        showErrorMessage('載入會員詳情失敗');
    } finally {
        loading.value = false;
    }
};

// 進入編輯模式
const enterEditMode = async () => {
    if (!canEdit.value && !canEditReferralOnly.value) {
        message.warning('您沒有編輯此會員的權限');
        return;
    }
    if (!memberDetail.value) return;
    editForm.value = {
        name: memberDetail.value.name || '',
        email: memberDetail.value.email || '',
        phone: memberDetail.value.phone || '',
        // 後端多半用英文，編輯顯示中文
        gender:
            memberDetail.value.gender === 'male'
                ? '男性'
                : memberDetail.value.gender === 'female'
                  ? '女性'
                  : memberDetail.value.gender || '',
        birthdate: memberDetail.value.birthdate || null,
        id_number: memberDetail.value.id_number || '',
        line_id: memberDetail.value.line_id || '',
        identity: memberDetail.value.identity || '',
        status: memberDetail.value.status ?? true,
        rank_id:
            memberDetail.value.rank_id != null
                ? String(memberDetail.value.rank_id)
                : null,
        department_id:
            memberDetail.value.department_id != null
                ? String(memberDetail.value.department_id)
                : null,
        referral_department_id:
            memberDetail.value.referral_department_id != null
                ? String(memberDetail.value.referral_department_id)
                : null,
        referral_id:
            memberDetail.value.referral_id != null
                ? String(memberDetail.value.referral_id)
                : null,
        recruiter_id:
            memberDetail.value.recruiter_id != null
                ? String(memberDetail.value.recruiter_id)
                : null,
        mentor_id:
            memberDetail.value.mentor_id != null
                ? String(memberDetail.value.mentor_id)
                : null
    };
    // 起聘日期（partner_assigned_at 可能含時間，取前 10 碼）
    editForm.value.assigned_at = memberDetail.value.partner_assigned_at
        ? String(memberDetail.value.partner_assigned_at).slice(0, 10)
        : null;
    editForm.value.partner_status = !!memberDetail.value.partner_status;
    // 將目前值補入 options，確保編輯時顯示預選
    ensureOption(
        rankOptions,
        memberDetail.value.rank_id,
        memberDetail.value.rank
    );
    ensureOption(
        departmentOptions,
        memberDetail.value.department_id,
        memberDetail.value.department
    );
    ensureOption(
        departmentOptions,
        memberDetail.value.referral_department_id,
        memberDetail.value.referral_department
    );
    ensureOption(
        referralOptions,
        memberDetail.value.referral_id,
        memberDetail.value.referral
    );
    ensureOption(
        recruiterOptions,
        memberDetail.value.recruiter_id,
        memberDetail.value.recruiter
    );
    ensureOption(
        mentorOptions,
        memberDetail.value.mentor_id,
        memberDetail.value.mentor
    );

    // 若 *_id 缺失，嘗試以名稱對應 options 取得 id
    const findIdByLabel = (opts, label) => {
        if (!label) return null;
        const o = (opts.value || []).find((x) => x.label === label);
        return o ? o.value : null;
    };
    if (!editForm.value.rank_id && memberDetail.value.rank) {
        editForm.value.rank_id = findIdByLabel(
            rankOptions,
            memberDetail.value.rank
        );
    }
    if (!editForm.value.department_id && memberDetail.value.department) {
        editForm.value.department_id = findIdByLabel(
            departmentOptions,
            memberDetail.value.department
        );
    }

    // 推薦/輔導人：只有名稱時，搜尋一次拿到 id
    const resolveUserIdByName = async (name) => {
        try {
            if (!name) return null;
            const keyword = String(name).trim();
            // 如果只有一個字，不呼叫 API，避免錯誤
            if (keyword.length < 2) return null;
            const res = await searchUsers({
                name: keyword,
                limit: 1
            });
            const arr = Array.isArray(res?.data)
                ? res.data
                : res?.data?.data || [];
            return arr.length > 0 ? String(arr[0].id) : null;
        } catch (e) {
            return null;
        }
    };
    if (!editForm.value.referral_id && memberDetail.value.referral) {
        const id = await resolveUserIdByName(memberDetail.value.referral);
        if (id) {
            editForm.value.referral_id = id;
            ensureOption(referralOptions, id, memberDetail.value.referral);
        }
    }
    if (!editForm.value.mentor_id && memberDetail.value.mentor) {
        const id = await resolveUserIdByName(memberDetail.value.mentor);
        if (id) {
            editForm.value.mentor_id = id;
            ensureOption(mentorOptions, id, memberDetail.value.mentor);
        }
    }
    referralDepartmentPreview.value =
        memberDetail.value.referral_department || '';
    hydratedReferralId.value = editForm.value.referral_id
        ? String(editForm.value.referral_id)
        : '';
    hydratedMentorId.value = editForm.value.mentor_id
        ? String(editForm.value.mentor_id)
        : '';
    isEditing.value = true;
};

// 取消編輯
const cancelEdit = () => {
    isEditing.value = false;
    editForm.value = {};
};

// 身分切換時的欄位清空/狀態預設
watch(
    () => editForm.value.identity,
    (val) => {
        if (!isEditing.value) return;
        if (val === 'student' || val === 'guest') {
            editForm.value.mentor_id = null;
            editForm.value.rank_id = null;
            editForm.value.assigned_at = null;
            editForm.value.partner_status = false;
        } else if (val === 'partner') {
            editForm.value.partner_status = true;
        }
    }
);

// 儲存會員資料
// 簡易驗證器
const isValidEmail = (email) =>
    /^[\w.!#$%&'*+/=?^`{|}~-]+@[\w-]+(\.[\w-]+)+$/.test(
        String(email || '').trim()
    );
const isValidPhone = (phone) => /^09\d{8}$/.test(String(phone || '').trim());
const isValidIdNumber = (id) => isValidTaiwanIdNumber(id);

const onReferralSearch = (val) => {
    clearTimeout(referralSearchTimer);
    referralSearchTimer = setTimeout(async () => {
        try {
            const keyword = String(val || '').trim();
            // 若輸入為空或只有一個字，直接清空選項並避免呼叫 API
            if (!keyword || keyword.length < 2) {
                referralOptions.value = [];
                return;
            }
            referralLoading.value = true;
            const res = await searchUsers({
                name: keyword,
                limit: 10
            });
            const arr = Array.isArray(res?.data)
                ? res.data
                : res?.data?.data || [];
            referralOptions.value = arr.map((u) => ({
                label: u.name || `#${u.id}`,
                value: String(u.id)
            }));
        } catch (e) {
            // 靜默失敗
        } finally {
            referralLoading.value = false;
        }
    }, USER_SEARCH_DEBOUNCE_MS);
};

const onRecruiterSearch = (val) => {
    clearTimeout(recruiterSearchTimer);
    recruiterSearchTimer = setTimeout(async () => {
        try {
            const keyword = String(val || '').trim();
            if (!keyword || keyword.length < 2) {
                recruiterOptions.value = [];
                return;
            }
            recruiterLoading.value = true;
            const res = await searchUsers({
                name: keyword,
                limit: 10
            });
            const arr = Array.isArray(res?.data)
                ? res.data
                : res?.data?.data || [];
            recruiterOptions.value = arr.map((u) => ({
                label: u.name || `#${u.id}`,
                value: String(u.id)
            }));
        } catch (e) {
            // 靜默失敗
        } finally {
            recruiterLoading.value = false;
        }
    }, USER_SEARCH_DEBOUNCE_MS);
};

const copyEditReferralToRecruiter = () => {
    const referralId = editForm.value?.referral_id;
    if (!referralId) {
        message.warning('請先選擇推薦人');
        return;
    }
    const id = referralId;
    const opt = referralOptions.value.find(
        (o) => String(o.value) === String(id)
    );
    if (
        opt &&
        !recruiterOptions.value.some((o) => String(o.value) === String(id))
    ) {
        recruiterOptions.value = [opt, ...recruiterOptions.value];
    }
    editForm.value.recruiter_id = String(id);
    message.success('已複製為推薦人');
};

const copyEditReferralDeptFromReferrer = async ({ quiet = false } = {}) => {
    const referralId = editForm.value?.referral_id;
    if (!referralId) {
        if (!quiet) message.warning('請先選擇推薦人');
        return;
    }
    try {
        const res = await getUserDetail(referralId);
        const data = res?.data || {};
        const deptId =
            data.referral_department_id ??
            data.partner?.referral_department_id ??
            data.department_id ??
            data.partner?.department_id ??
            null;
        const deptName =
            data.referral_department ||
            data.partner?.referral_department ||
            data.department ||
            data.partner?.department ||
            '';
        if (!deptId) {
            editForm.value.referral_department_id = null;
            referralDepartmentPreview.value = '';
            message.warning('該推薦人尚未設定推薦事業部');
            return;
        }
        editForm.value.referral_department_id = String(deptId);
        referralDepartmentPreview.value = deptName;
        if (!quiet) message.success('已帶入推薦人的推薦事業部');
    } catch (e) {
        console.error(e);
        if (!quiet) showErrorMessage('取得推薦人資料失敗');
    }
};

watch(
    () => editForm.value?.referral_id,
    (id) => {
        if (!isEditing.value || canEditReferralOnly.value) return;
        const next = id ? String(id) : '';
        if (!next || next === hydratedReferralId.value) return;
        hydratedReferralId.value = next;
        copyEditReferralDeptFromReferrer({ quiet: true });
    }
);

const copyEditMentorDeptFromMentor = async ({ quiet = false } = {}) => {
    const mentorId = editForm.value?.mentor_id;
    if (!mentorId) {
        if (!quiet) message.warning('請先選擇輔導人');
        return;
    }
    try {
        const res = await getUserDetail(mentorId);
        const data = res?.data || {};
        const deptId =
            data.department_id ??
            data.partner?.department_id ??
            null;
        if (!deptId) {
            if (!quiet) message.warning('該輔導人尚未設定輔導事業部');
            return;
        }
        editForm.value.department_id = String(deptId);
        if (!quiet) message.success('已帶入輔導人的輔導事業部');
    } catch (e) {
        console.error(e);
        if (!quiet) showErrorMessage('取得輔導人資料失敗');
    }
};

watch(
    () => editForm.value?.mentor_id,
    (id) => {
        if (!isEditing.value || !canEditPartnerOrg.value) return;
        const next = id ? String(id) : '';
        if (!next || next === hydratedMentorId.value) return;
        hydratedMentorId.value = next;
        copyEditMentorDeptFromMentor({ quiet: true });
    }
);

const onMentorSearch = (val) => {
    clearTimeout(mentorSearchTimer);
    mentorSearchTimer = setTimeout(async () => {
        try {
            const keyword = String(val || '').trim();
            // 若輸入為空或只有一個字，直接清空選項並避免呼叫 API
            if (!keyword || keyword.length < 2) {
                mentorOptions.value = [];
                return;
            }
            mentorLoading.value = true;
            const res = await searchUsers({
                name: keyword,
                limit: 10
            });
            const arr = Array.isArray(res?.data)
                ? res.data
                : res?.data?.data || [];
            mentorOptions.value = arr.map((u) => ({
                label: u.name || `#${u.id}`,
                value: String(u.id)
            }));
        } catch (e) {
            // 靜默失敗
        } finally {
            mentorLoading.value = false;
        }
    }, USER_SEARCH_DEBOUNCE_MS);
};

const saveMemberDetail = async () => {
    if (!canEdit.value && !canEditReferralOnly.value) {
        message.warning('您沒有編輯此會員的權限');
        return;
    }
    if (canEditReferralOnly.value && !canEdit.value) {
        const userId = route.params.id;
        const referralId = editForm.value?.referral_id;
        if (!userId || !referralId) {
            message.warning('請先選擇推薦人');
            return;
        }
        try {
            saving.value = true;
            const response = await updateUser(userId, {
                partner: { referral_id: parseInt(referralId, 10) }
            });
            if (response && (response.result || response.success)) {
                message.success('已更新推薦人，推薦事業部已依推薦線重算');
                await loadMemberDetail();
                isEditing.value = false;
            } else {
                showErrorMessage(response?.message || '更新失敗');
            }
        } catch (e) {
            console.error(e);
            showErrorMessage('更新失敗');
        } finally {
            saving.value = false;
        }
        return;
    }
    const userId = route.params.id;
    if (!userId) {
        showErrorMessage('缺少會員 ID');
        return;
    }

    try {
        saving.value = true;
        // 前端驗證
        const data = { ...editForm.value };
        if (data.email && !isValidEmail(data.email)) {
            showErrorMessage('Email 格式不正確');
            return;
        }
        if (data.phone && !isValidPhone(data.phone)) {
            showErrorMessage('手機號碼格式需為 09xxxxxxxx');
            return;
        }
        if (data.identity === 'partner' && !data.recruiter_id) {
            showErrorMessage('請選擇招募人');
            return;
        }
        if (
            canViewSensitivePii.value &&
            data.id_number &&
            !isValidIdNumber(data.id_number.toUpperCase())
        ) {
            showErrorMessage(`${TAIWAN_ID_LABEL}格式不正確`);
            return;
        }
        if (data.identity === 'partner' && !data.recruiter_id) {
            showErrorMessage('請選擇招募人');
            return;
        }

        // 性別中文/英文 -> 整數 (0:女性,1:男性)
        if (data.gender === '男性' || data.gender === 'male') data.gender = 1;
        else if (data.gender === '女性' || data.gender === 'female')
            data.gender = 0;

        // 生日格式（DatePicker 值可能是 dayjs/string）
        if (data.birthdate && typeof data.birthdate !== 'string') {
            try {
                data.birthdate = data.birthdate.format
                    ? data.birthdate.format('YYYY-MM-DD')
                    : new Date(data.birthdate).toISOString().slice(0, 10);
            } catch (e) {
                // 若轉換失敗則保持原狀
            }
        }

        // 起聘日期格式（DatePicker 值可能是 dayjs/string）
        if (data.assigned_at && typeof data.assigned_at !== 'string') {
            try {
                data.assigned_at = data.assigned_at.format
                    ? data.assigned_at.format('YYYY-MM-DD')
                    : new Date(data.assigned_at).toISOString().slice(0, 10);
            } catch (e) {
                // 若轉換失敗則保持原狀
            }
        }

        // 不阻擋送出：有填即可，由後端再做嚴格驗證；避免 UI 誤判型別造成無法送出

        // 構建提交欄位（符合 API 規格）；生日清空時帶 null 給 API
        // 帳號黑名單（status）改走專用 API（必填原因），儲存時不帶 status
        const payload = {
            name: data.name,
            email: data.email,
            phone: data.phone || undefined,
            line_id: data.line_id || undefined,
            identity: data.identity || undefined,
            gender:
                data.gender === 0 || data.gender === 1 ? data.gender : undefined
        };

        // 僅管理者視角可讀寫身分證／生日；其餘視角勿帶欄位以免誤清空
        if (canViewSensitivePii.value) {
            payload.birthdate =
                data.birthdate && String(data.birthdate).trim()
                    ? data.birthdate
                    : null;
            payload.id_number =
                data.id_number && data.id_number.trim()
                    ? String(data.id_number).toUpperCase().trim()
                    : null;
        }

        // partner 物件（需支援清空：以 null 明示）
        const partner = {};
        const toIntOrNull = (v) =>
            v === undefined || v === ''
                ? null
                : v === null
                  ? null
                  : parseInt(v, 10);

        if (data.identity === 'partner') {
            partner.status = true;
            partner.rank_id = toIntOrNull(data.rank_id); // 無值 -> null
            // allow-clear 後為 undefined；完整表單送出時一律帶 key，以 null 明示清空
            partner.department_id = toIntOrNull(data.department_id);
            partner.referral_department_id = toIntOrNull(
                data.referral_department_id
            );
            partner.referral_id = toIntOrNull(data.referral_id);
            partner.recruiter_id = toIntOrNull(data.recruiter_id);
            partner.mentor_id = toIntOrNull(data.mentor_id);
            partner.assigned_at = data.assigned_at || null;
        } else if (data.identity === 'student' || data.identity === 'guest') {
            partner.status = false;
            partner.rank_id = null;
            partner.mentor_id = null;
            partner.assigned_at = null;
            // student/guest 可設定/清空事業部（清空必須送 null，不可省略 key）
            partner.department_id = toIntOrNull(data.department_id);
            partner.referral_department_id = toIntOrNull(
                data.referral_department_id
            );
            // 允許學員/訪客更新推薦人／招募人
            partner.referral_id = toIntOrNull(data.referral_id);
            partner.recruiter_id = toIntOrNull(data.recruiter_id);
        }

        // 無論哪個身份，都帶 partner，讓後端可據此清空/更新
        payload.partner = partner;

        const response = await updateUser(userId, payload);
        if (response && (response.result || response.success)) {
            message.success('更新成功');
            await loadMemberDetail();
            isEditing.value = false;
        } else {
            showErrorMessage(response?.message || '更新失敗');
        }
    } catch (error) {
        console.error('更新會員資料失敗:', error);
        showErrorMessage('更新會員資料失敗');
    } finally {
        saving.value = false;
    }
};

onMounted(() => {
    loadMemberDetail();
    // 載入下拉選項
    fetchAllRankRows()
        .then((rows) => {
            rankOptions.value = toRankOptions(rows, { valueAsString: true });
        })
        .catch(() => (rankOptions.value = []));
    fetchAllDepartmentRows()
        .then((rows) => {
            departmentOptions.value = toDepartmentOptions(rows, {
                valueAsString: true
            });
        })
        .catch(() => (departmentOptions.value = []));
    // 積分類型選項（積分紀錄篩選）
    fetchPointTypeOptions();
});

// 同一元件被 keep-alive 重用、或 params 變更時重新載入（避免仍顯示上一筆會員）
watch(
    () => route.params.id,
    (id, prevId) => {
        if (id && String(id) !== String(prevId ?? '')) {
            isEditing.value = false;
            loadMemberDetail();
        }
    }
);

// 進到積分紀錄分頁時自動載入資料
watch(
    () => activeTab.value,
    (tab) => {
        if (tab === 'points') fetchPointLogs();
        if (tab === 'referral') fetchReferralLogs();
        if (tab === 'department') fetchDepartmentLogs();
        if (tab === 'rank') fetchRankLogs();
        if (tab === 'level') fetchLevelLogs();
    },
    { immediate: false }
);
</script>

<style scoped>
.page {
    padding: 24px;
    background: #ffffff;
    min-height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    font-family: 'Courier New', monospace;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    text-shadow: 0 0 20px rgba(42, 74, 122, 0.3);
    letter-spacing: 2px;
}

.page-header p {
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
    margin: 0;
}

.content-grid {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 24px;
    flex: 1;
    min-height: calc(100vh - 180px);
    overflow: hidden;
}

.record-content--system-access {
    padding: 24px 24px 32px;
}

.system-access-layout {
    width: 100%;
    max-width: 520px;
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: left;
}

.system-access-hint {
    font-size: 13px;
    color: var(--muted, #5c6570);
    margin: 0 0 16px 0;
}

.system-access-hint--sub {
    margin-top: -10px;
    margin-bottom: 16px;
    line-height: 1.55;
}

.system-access-zone {
    margin-bottom: 18px;
    border: 1px solid var(--type-system-border, rgba(42, 74, 122, 0.35));
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: none;
}

.system-access-zone__head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    background: var(--type-system-bg, #e8eef5);
    border-bottom: 1px solid var(--line, rgba(28, 34, 40, 0.1));
}

.system-access-zone__num {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: var(--type-system, #2a4a7a);
    color: #ffffff;
    font-weight: 800;
    font-size: 15px;
    line-height: 30px;
    text-align: center;
}

.system-access-zone__titles {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    text-align: left;
}

.system-access-zone__title {
    font-weight: 700;
    font-size: 15px;
    color: var(--ink, #1c2228);
}

.system-access-zone__body {
    padding: 14px 16px 16px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    text-align: left;
}

.system-access-zone__divider {
    width: 100%;
    height: 0;
    border-top: 1px dashed var(--line-strong, rgba(28, 34, 40, 0.16));
    margin: 2px 0 4px;
}

.system-access-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-soft, #3a4450);
}

.system-access-muted {
    font-size: 12px;
    color: var(--muted, #5c6570);
    margin: 0;
}

.system-access-actions {
    padding-top: 4px;
    display: flex;
    justify-content: flex-start;
}

.system-access-save-btn {
    min-width: 120px;
}

/* 會員可用系統：身份選項直向排列，較易閱讀 */
.system-access-role-group :deep(.ant-checkbox-group) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
}

.system-access-role-group :deep(.ant-checkbox-wrapper) {
    margin-inline-end: 0;
}

/* 左邊功能選單面板 */
.menu-panel {
    background: #ffffff;
    border: 2px solid #2a4a7a;
    border-radius: 16px;
    padding: 24px;
    position: relative;
    box-shadow: 0 0 40px rgba(42, 74, 122, 0.2);
    overflow-y: auto;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.2);
}

.header-actions {
    display: flex;
    gap: 8px;
}

.panel-header h4 {
    color: #2a4a7a;
    font-size: 18px;
    font-weight: 700;
    margin: 0;
    text-shadow: 0 0 15px rgba(42, 74, 122, 0.6);
    font-family: 'Courier New', monospace;
    letter-spacing: 1px;
}

.refresh-button {
    background: #ffffff;
    border: 2px solid #2a4a7a;
    color: #2a4a7a;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.refresh-button:hover {
    border-color: #64748b;
    color: #64748b;
    background: rgba(255, 158, 207, 0.1);
    box-shadow: 0 0 10px rgba(255, 158, 207, 0.3);
}

.menu-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.menu-item {
    display: flex;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.8),
        rgba(248, 249, 250, 0.8)
    );
    border: 2px solid #d0d0d0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Courier New', monospace;
}

.menu-item:hover {
    border-color: #64748b;
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.1),
        rgba(42, 74, 122, 0.1)
    );
    box-shadow: 0 0 15px rgba(255, 158, 207, 0.3);
    transform: translateX(4px);
}

.menu-item.active {
    border-color: #2a4a7a;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.15),
        rgba(138, 175, 255, 0.15)
    );
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.4);
    transform: translateX(8px);
}

.menu-item.active .menu-text {
    color: #2a4a7a;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.6);
}

.menu-text {
    color: #2a2a3a;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.no-permission {
    padding: 40px 20px;
    text-align: center;
}

.no-permission p {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.info-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Courier New', monospace;
    display: grid;
    grid-template-columns: 160px 1fr;
    column-gap: 24px;
    grid-auto-rows: auto;
    row-gap: 8px;
}

.info-table tbody {
    display: contents;
}

.info-table tr {
    display: contents;
    /* 讓每列的單元格直接參與 grid 佈局，便於左右並排 */
}

.info-table tr:last-child {
    border-bottom: none;
}

.info-table td {
    display: block;
    /* 讓格子成為 grid item */
    padding: 12px 0;
}

.info-label {
    color: #6a6a7a;
    font-size: 14px;
    font-weight: 500;
    width: 100%;
    padding-right: 16px;
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.org-field-label {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
}

.org-copy-link {
    font-size: 12px;
    font-weight: 500;
    color: #9b6dff;
    text-decoration: none;
    line-height: 1;
    white-space: nowrap;
}

.org-copy-link:hover {
    color: #7a4dff;
    text-decoration: underline;
}

.org-copy-link.is-disabled {
    color: rgba(42, 42, 58, 0.28);
    pointer-events: none;
    text-decoration: none;
    cursor: not-allowed;
}

.info-value {
    color: #2a2a3a;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
}

.status-active {
    color: #52c41a;
}

.status-inactive {
    color: #ff4d4f;
}

.identity-block-reason {
    margin-left: 8px;
    font-size: 12px;
    color: #d4380d;
}

.verified-row {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.icon-verified {
    color: #52c41a;
    font-size: 16px;
}

.icon-unverified {
    color: #ff4d4f;
    font-size: 16px;
}

/* 組織圖 Modal：與事業部管理組織圖一致（邊框、標題、容器樣式） */
.orgchart-modal :deep(.ant-modal-content) {
    background: rgba(255, 255, 255, 0.98);
    border: 2px solid #2a4a7a;
    box-shadow: 0 0 40px rgba(42, 74, 122, 0.3);
}

.orgchart-modal :deep(.ant-modal-body) {
    background: rgba(255, 255, 255, 0.98);
    padding: 24px;
}

.orgchart-modal :deep(.org-chart-wrapper) {
    min-height: 72vh;
    background: #ffffff;
}

/* 與事業部管理 .tree-section 一致 */
.orgchart-modal .tree-section {
    background: #ffffff;
    border: 2px solid #2a4a7a;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3);
}

.orgchart-modal .tree-section-title {
    color: #2a4a7a;
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 16px 0;
    text-shadow: 0 0 15px rgba(42, 74, 122, 0.4);
    font-family: 'Courier New', monospace;
    letter-spacing: 1px;
}

.orgchart-modal .tree-container {
    min-height: 200px;
}

/* 夥伴組織圖按鈕：淺色背景與邊框 */
.orgchart-btn {
    background: rgba(42, 74, 122, 0.08);
    border: 2px solid rgba(42, 74, 122, 0.35);
    color: #2a4a7a;
    border-radius: 9999px;
}

.orgchart-btn:hover {
    background: rgba(42, 74, 122, 0.12);
    border-color: #2a4a7a;
    box-shadow: 0 0 12px rgba(42, 74, 122, 0.35);
}

.orgchart-btn :deep(.anticon) {
    color: #2a4a7a;
}

/* 右邊內容面板 */
.content-panel {
    background: #ffffff;
    border: 2px solid #2a4a7a;
    border-radius: 16px;
    padding: 24px;
    position: relative;
    box-shadow: 0 0 40px rgba(42, 74, 122, 0.2);
    overflow-y: auto;
}

.points-grid {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 16px;
}

@media (min-width: 768px) {
    .points-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 1200px) {
    .points-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

.points-card {
    border: 2px solid #2a4a7a;
    border-radius: 12px;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.25);
}

.points-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.points-code {
    font-weight: 800;
    color: #2a4a7a;
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.5);
}

.points-total-tag {
    background: linear-gradient(120deg, #ffa87a, #0e7490);
    border: none;
    color: #0d0d0d;
    font-weight: 700;
}

.points-metrics {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}

.metric {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}

.metric-label {
    color: #6a6a7a;
}

.metric-value {
    font-weight: 800;
}

.metric-value.usable {
    color: #52c41a;
}

.metric-value.frozen {
    color: #ff4d4f;
}

/* 蒸氣波按鈕樣式（與部門頁一致） */
.vaporwave-button {
    background: linear-gradient(45deg, #64748b, #2a4a7a);
    border: none;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: bold;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.4);
    transition: all 0.3s ease;
}

.vaporwave-button:hover {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5);
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.6);
    transform: translateY(-2px);
}

/* 寬螢幕：基本資料左右佈局，避免過長 */
@media (min-width: 1200px) {
    .info-table {
        grid-template-columns: 160px 1fr 160px 1fr;
        /* 兩組 label-value 並排 */
        column-gap: 32px;
    }
}

.content-area {
    min-height: 200px;
}

.detail-content {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.info-section {
    display: flex;
    flex-direction: column;
}

/* 統一搜尋欄位樣式 */
.info-section :deep(.status-select) {
    height: 32px !important;
    font-size: 13px !important;
    border-radius: 8px !important;
}

.info-section :deep(.status-select .ant-select-selector),
.info-section :deep(.status-select .ant-picker) {
    height: 32px !important;
    line-height: 30px !important;
    font-size: 13px !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    background: rgba(255, 255, 255, 0.95) !important;
    color: #2a2a3a !important;
    transition: all 0.3s ease !important;
}

.info-section :deep(.status-select .ant-select-selector:hover),
.info-section :deep(.status-select .ant-picker:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
}

.info-section :deep(.status-select.ant-select-focused .ant-select-selector),
.info-section :deep(.status-select.ant-picker-focused) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.info-section :deep(.status-select .ant-select-selection-placeholder),
.info-section :deep(.status-select .ant-picker-input input::placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
    font-size: 13px !important;
}

.section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a4a7a;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
    font-family: 'Courier New', monospace;
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.4);
}

.record-content {
    padding: 0;
    text-align: left;
    min-height: auto;
    display: block;
    width: 100%;
}

.placeholder-text {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    text-align: center;
    padding: 40px 0;
}

/* 任務紀錄樣式 */
.task-records-content {
    padding: 0;
    min-height: auto;
    display: block;
}

.task-toolbar {
    margin-bottom: 24px;
}

.task-filter-collapse {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
    overflow: hidden;
    margin-bottom: 16px;
    position: relative;
}

.task-filter-collapse::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    opacity: 0.8;
    z-index: 0;
    pointer-events: none;
}

.task-filter-collapse :deep(.ant-collapse-item) {
    border: none;
    background: transparent;
    position: relative;
    z-index: 1;
}

.task-filter-collapse :deep(.ant-collapse-header) {
    padding: 16px 24px;
    background: transparent;
    border-bottom: 1px solid rgba(42, 74, 122, 0.1);
}

.task-filter-collapse :deep(.ant-collapse-content) {
    background: transparent;
    border: none;
}

.task-filter-collapse :deep(.ant-collapse-content-box) {
    padding: 20px 24px;
    position: relative;
    z-index: 1;
}

.task-filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.task-filter-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.task-filters-content {
    padding: 0;
}

.task-filter-group {
    margin-bottom: 20px;
}

.task-filter-group:last-child {
    margin-bottom: 0;
}

.task-filter-group-title {
    font-weight: 600;
    color: #5a6578;
    margin-bottom: 12px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    text-align: left;
}

.task-filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.task-filter-item {
    flex: 1;
    min-width: 180px;
}

/* 任務紀錄篩選欄位統一樣式 */
.task-filters-content :deep(.status-select) {
    height: 32px !important;
    font-size: 13px !important;
    border-radius: 8px !important;
}

.task-filters-content :deep(.status-select .ant-select-selector),
.task-filters-content :deep(.status-select .ant-picker) {
    height: 32px !important;
    line-height: 30px !important;
    font-size: 13px !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    background: rgba(255, 255, 255, 0.95) !important;
    color: #2a2a3a !important;
    transition: all 0.3s ease !important;
}

.task-filters-content :deep(.status-select .ant-select-selector:hover),
.task-filters-content :deep(.status-select .ant-picker:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
}

.task-filters-content
    :deep(.status-select.ant-select-focused .ant-select-selector),
.task-filters-content :deep(.status-select.ant-picker-focused) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.task-filters-content :deep(.status-select .ant-select-selection-placeholder),
.task-filters-content
    :deep(.status-select .ant-picker-input input::placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
    font-size: 13px !important;
}

.task-toolbar-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
}

.task-view-mode-switch {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid rgba(42, 74, 122, 0.25);
    border-radius: 10px;
    padding: 4px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15);
}

.task-view-mode-switch :deep(.ant-radio-button-wrapper) {
    border: none !important;
    background: transparent !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    transition: all 0.3s ease;
    border-radius: 7px;
    padding: 4px 12px;
    height: auto;
    line-height: 1.5;
}

.task-view-mode-switch :deep(.ant-radio-button-wrapper:hover) {
    color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.1) !important;
}

.task-view-mode-switch :deep(.ant-radio-button-wrapper-checked) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    color: #ffffff !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.task-view-mode-switch :deep(.ant-radio-button-wrapper-checked:hover) {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
}

/* 蒸氣波風格按鈕 */
.vaporwave-btn {
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border: none;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.25),
        inset 0 0 12px rgba(255, 255, 255, 0.1);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
    height: 36px;
    padding: 0 20px;
    font-size: 14px;
}

.vaporwave-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
    );
    transition: left 0.5s ease;
}

.vaporwave-btn:hover::before {
    left: 100%;
}

.vaporwave-btn:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow:
        0 6px 24px rgba(42, 74, 122, 0.4),
        inset 0 0 15px rgba(255, 255, 255, 0.15);
}

.vaporwave-btn:active {
    transform: translateY(0) scale(1);
    box-shadow:
        0 2px 10px rgba(42, 74, 122, 0.3),
        inset 0 0 8px rgba(255, 255, 255, 0.1);
}

.vaporwave-btn.ant-btn-primary {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    color: #ffffff !important;
}

.vaporwave-btn.ant-btn-primary:hover {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
}

.vaporwave-btn :deep(.anticon) {
    transition: all 0.3s ease;
}

.vaporwave-btn:hover :deep(.anticon) {
    filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.6));
    transform: rotate(180deg);
}

.task-records-list-view {
    margin-top: 24px;
}

.task-progress-table :deep(.ant-table-tbody > tr) {
    cursor: pointer;
    transition: all 0.2s ease;
}

.task-progress-table :deep(.ant-table-tbody > tr:hover) {
    background: rgba(42, 74, 122, 0.05) !important;
}

.task-progress-table :deep(.ant-table-tbody > tr > td) {
    padding: 12px 16px;
}

.task-tree-stats-view {
    margin-top: 24px;
}

.task-loading-container,
.task-empty-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

/* 主線網格佈局 - 每個主線作為獨立的樹 */
.task-mission-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    padding: 0;
}

/* 主線卡片 */
.task-mission-card {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.15);
    border-radius: 16px;
    padding: 12px 16px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.08);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

/* 展開狀態的卡片 */
.task-mission-card-expanded {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border: 2px solid rgba(42, 74, 122, 0.3);
    padding: 16px;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.15);
}

.task-mission-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #64748b, #2a4a7a, #3b6ea5, #0e7490);
    background-size: 200% 100%;
    animation: gradientShift 3s ease infinite;
    opacity: 0.3;
}

.task-mission-card-expanded::before {
    opacity: 1;
}

.task-mission-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(42, 74, 122, 0.15);
    border-color: rgba(42, 74, 122, 0.25);
}

.task-mission-card-expanded:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(42, 74, 122, 0.2);
    border-color: rgba(42, 74, 122, 0.4);
}

/* 主線標題區 */
.task-mission-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.2);
}

.task-mission-title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
}

.task-mission-expand-btn {
    color: #2a4a7a !important;
    padding: 0 4px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.task-mission-expand-btn:hover {
    color: #3b6ea5 !important;
    background: rgba(42, 74, 122, 0.1) !important;
    border-radius: 4px;
}

.task-mission-tag {
    flex-shrink: 0;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    font-size: 13px;
}

.task-mission-name {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    word-break: break-word;
}

.task-mission-status {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

/* 收合狀態提示 */
.task-mission-collapsed-hint {
    padding: 12px 16px;
    text-align: center;
    background: rgba(42, 74, 122, 0.05);
    border-radius: 8px;
    border: 1px dashed rgba(42, 74, 122, 0.2);
    margin-top: 8px;
}

.hint-text {
    font-size: 13px;
    color: #718096;
    font-family: 'Courier New', monospace;
    font-style: italic;
}

.task-mission-time {
    font-size: 12px;
    color: #718096;
    font-family: 'Courier New', monospace;
    white-space: nowrap;
}

/* 樹狀容器 */
.task-tree-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0;
}

.task-tree-node-item {
    display: flex;
    flex-direction: column;
}

.task-tree-node-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-radius: 8px;
    transition: all 0.2s ease;
    margin-bottom: 2px;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid transparent;
}

.task-tree-node-row:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(42, 74, 122, 0.25);
    box-shadow: 0 2px 6px rgba(42, 74, 122, 0.08);
}

.task-tree-node-clickable {
    cursor: pointer;
}

.task-tree-node-clickable:hover {
    transform: translateX(2px);
    background: rgba(255, 255, 255, 0.95) !important;
    border-color: rgba(42, 74, 122, 0.35) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15) !important;
}

.task-status-tag-clickable {
    cursor: pointer;
    transition: all 0.2s ease;
}

.task-status-tag-clickable:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.3);
}

/* 不同層級的縮排和顏色 - 在卡片內重新調整，移除左側邊框 */
.task-tree-level-1 {
    padding-left: 20px;
    background: linear-gradient(
        90deg,
        rgba(138, 175, 255, 0.06),
        rgba(255, 255, 255, 0.7)
    );
}

.task-tree-level-2 {
    padding-left: 40px;
    background: linear-gradient(
        90deg,
        rgba(168, 255, 246, 0.05),
        rgba(255, 255, 255, 0.7)
    );
}

.task-tree-level-3 {
    padding-left: 60px;
    background: linear-gradient(
        90deg,
        rgba(208, 255, 183, 0.04),
        rgba(255, 255, 255, 0.7)
    );
}

.task-tree-node-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.task-tree-node-tag {
    flex-shrink: 0;
    font-weight: 600;
    font-family: 'Courier New', monospace;
}

.task-tree-node-name {
    font-size: 14px;
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    word-break: break-word;
}

.task-tree-node-status {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

.task-tree-node-time {
    font-size: 12px;
    color: #718096;
    font-family: 'Courier New', monospace;
    white-space: nowrap;
}

.task-tree-children {
    margin-top: 2px;
    margin-left: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

/* 節點詳細歷程 Modal 樣式 */
.task-node-detail-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.task-node-detail-info {
    background: rgba(248, 249, 250, 0.6);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(156, 163, 175, 0.15);
}

.task-node-detail-progress {
    margin-top: 8px;
}

.task-node-detail-section-title {
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.2);
}

.task-node-detail-empty {
    padding: 40px 0;
    text-align: center;
}

.task-node-detail-progress-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.task-node-detail-progress-item {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 10px;
    padding: 14px;
    transition: all 0.2s ease;
    cursor: pointer;
}

.task-node-detail-progress-item:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(42, 74, 122, 0.35);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
    transform: translateX(4px);
}

.task-progress-item-selected {
    background: rgba(42, 74, 122, 0.1) !important;
    border-color: rgba(42, 74, 122, 0.5) !important;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.2) !important;
}

.task-progress-item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.task-progress-item-time {
    font-size: 12px;
    color: #718096;
    font-family: 'Courier New', monospace;
}

.task-progress-item-conditions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
}

.task-progress-conditions-text {
    color: #4a5568;
    font-size: 13px;
    font-family: 'Courier New', monospace;
}

.task-progress-pending-review {
    color: #ffa87a;
    font-size: 12px;
    font-weight: 500;
    font-family: 'Courier New', monospace;
}

.task-progress-pending-review.clickable {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.3s ease;
}

.task-progress-pending-review.clickable:hover {
    color: #ff8a5a;
    text-decoration: underline;
}

.task-progress-item-time-info {
    font-size: 12px;
    color: #718096;
    font-family: 'Courier New', monospace;
    margin-top: 6px;
    line-height: 1.6;
}

/* 詳細資訊區樣式 */
.task-node-detail-details {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 2px solid rgba(42, 74, 122, 0.2);
}

.task-node-detail-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
}

.task-node-detail-details-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.task-detail-section {
    background: rgba(248, 249, 250, 0.6);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid rgba(42, 74, 122, 0.15);
}

.task-detail-conditions-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
}

.task-detail-condition-item {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 10px;
    padding: 14px;
    transition: all 0.2s ease;
}

.task-detail-condition-item:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(42, 74, 122, 0.3);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.task-condition-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.task-condition-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.task-condition-description {
    font-size: 14px;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    text-align: left;
}

.task-condition-time,
.task-condition-review,
.task-condition-note {
    font-size: 12px;
    color: #718096;
    font-family: 'Courier New', monospace;
    margin-top: 8px;
    line-height: 1.6;
}

.task-condition-review-time {
    color: #a0aec0;
}

.task-condition-note {
    color: #4a5568;
    font-style: italic;
    padding: 8px;
    background: rgba(42, 74, 122, 0.05);
    border-radius: 6px;
    margin-top: 10px;
}

/* 時間線樣式 */
.task-detail-timeline {
    margin-top: 16px;
    padding-left: 24px;
    position: relative;
}

.task-detail-timeline::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(
        180deg,
        rgba(42, 74, 122, 0.3),
        rgba(138, 175, 255, 0.3)
    );
}

.task-timeline-item {
    position: relative;
    margin-bottom: 20px;
    padding-left: 24px;
}

.task-timeline-dot {
    position: absolute;
    left: -16px;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    border: 2px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.5);
}

.task-timeline-content {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    padding: 12px 16px;
    transition: all 0.2s ease;
}

.task-timeline-content:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(42, 74, 122, 0.3);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.task-timeline-label {
    font-size: 14px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 6px;
}

.task-timeline-time {
    font-size: 12px;
    color: #718096;
    font-family: 'Courier New', monospace;
    margin-bottom: 6px;
}

.task-timeline-description {
    font-size: 13px;
    color: #4a5568;
    font-family: 'Courier New', monospace;
    line-height: 1.6;
}

/* 子進度列表樣式 */
.task-detail-child-progresses {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
}

.task-child-progress-item {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 10px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.2s ease;
}

.task-child-progress-item:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(42, 74, 122, 0.3);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.task-child-progress-name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.task-child-progress-time {
    font-size: 12px;
    color: #718096;
    font-family: 'Courier New', monospace;
}

/* Tab 樣式 */
.task-detail-tabs {
    margin-top: 16px;
}

.task-detail-tabs :deep(.ant-tabs-nav) {
    margin-bottom: 20px;
}

.task-detail-tabs :deep(.ant-tabs-tab) {
    font-family: 'Courier New', monospace;
    font-weight: 500;
    color: #4a5568;
    border: 1px solid rgba(42, 74, 122, 0.2);
    background: rgba(255, 255, 255, 0.7);
    transition: all 0.3s ease;
}

.task-detail-tabs :deep(.ant-tabs-tab:hover) {
    color: #2a4a7a;
    border-color: rgba(42, 74, 122, 0.4);
    background: rgba(42, 74, 122, 0.1);
}

.task-detail-tabs :deep(.ant-tabs-tab-active) {
    color: #2a4a7a;
    border-color: #2a4a7a;
    background: rgba(42, 74, 122, 0.15);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

.task-detail-tabs :deep(.ant-tabs-content-holder) {
    padding: 0;
}

.task-detail-tabs :deep(.ant-tabs-tabpane) {
    padding: 0;
}

.task-detail-tab-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.task-detail-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 20px;
    min-height: 200px;
}

/* 區塊標題樣式 */
.task-node-detail-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid rgba(42, 74, 122, 0.2);
}

.section-title-icon {
    font-size: 18px;
}

/* 描述列表樣式 */
.task-detail-descriptions {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
}

.task-detail-descriptions :deep(.ant-descriptions-item-label) {
    font-weight: 600;
    color: #4a5568;
    font-family: 'Courier New', monospace;
    background: rgba(42, 74, 122, 0.05);
}

.task-detail-descriptions :deep(.ant-descriptions-item-content) {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.time-value {
    font-family: 'Courier New', monospace;
    color: #4a5568;
}

.status-tag {
    font-weight: 600;
}

/* 層級路徑樣式 */
.task-hierarchy-path {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
    padding: 16px;
    border: 1px solid rgba(42, 74, 122, 0.2);
}

.hierarchy-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: rgba(42, 74, 122, 0.05);
    border-radius: 6px;
    border-left: 3px solid #2a4a7a;
}

.hierarchy-label {
    font-weight: 600;
    color: #4a5568;
    font-family: 'Courier New', monospace;
    min-width: 60px;
}

.hierarchy-value {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

/* 條件統計卡片 */
.task-conditions-summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 16px;
    margin-top: 16px;
}

.summary-card {
    background: rgba(255, 255, 255, 0.9);
    border-radius: 12px;
    padding: 20px;
    text-align: center;
    border: 2px solid;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.summary-card.total {
    border-color: rgba(138, 175, 255, 0.4);
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.1),
        rgba(255, 255, 255, 0.9)
    );
}

.summary-card.completed {
    border-color: rgba(82, 196, 26, 0.4);
    background: linear-gradient(
        135deg,
        rgba(82, 196, 26, 0.1),
        rgba(255, 255, 255, 0.9)
    );
}

.summary-card.pending {
    border-color: rgba(255, 193, 7, 0.4);
    background: linear-gradient(
        135deg,
        rgba(255, 193, 7, 0.1),
        rgba(255, 255, 255, 0.9)
    );
}

.summary-card.pending-review {
    border-color: rgba(255, 168, 122, 0.4);
    background: linear-gradient(
        135deg,
        rgba(255, 168, 122, 0.1),
        rgba(255, 255, 255, 0.9)
    );
}

.summary-card.rejected {
    border-color: rgba(255, 77, 79, 0.4);
    background: linear-gradient(
        135deg,
        rgba(255, 77, 79, 0.1),
        rgba(255, 255, 255, 0.9)
    );
}

.summary-card-value {
    font-size: 32px;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
    margin-bottom: 8px;
    line-height: 1;
}

.summary-card-label {
    font-size: 13px;
    color: #718096;
    font-family: 'Courier New', monospace;
    font-weight: 500;
}

/* 條件詳情樣式 */
.task-condition-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(42, 74, 122, 0.1);
}

.detail-label {
    font-weight: 600;
    color: #718096;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    margin-right: 6px;
}

.detail-value {
    color: #4a5568;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

.condition-type-tag,
.condition-status-tag {
    font-weight: 600;
}

/* 關聯進度項目 */
.task-relation-progress-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 8px;
    border: 1px solid rgba(42, 74, 122, 0.2);
}

.relation-progress-name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

/* 響應式設計 */
@media (max-width: 1200px) {
    .task-mission-grid {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    }
}

@media (max-width: 768px) {
    .task-mission-card {
        padding: 16px;
    }

    .task-mission-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .task-mission-status {
        width: 100%;
        justify-content: flex-start;
    }

    .task-tree-level-1 {
        padding-left: 20px;
    }

    .task-tree-level-2 {
        padding-left: 40px;
    }

    .task-tree-level-3 {
        padding-left: 60px;
    }

    .task-tree-node-row {
        padding: 8px 12px;
        flex-wrap: wrap;
    }

    .task-tree-node-status {
        width: 100%;
        margin-top: 8px;
        justify-content: flex-start;
    }
}

.task-node-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.task-node-cell:hover {
    opacity: 0.8;
}

.task-node-name {
    font-weight: 500;
    color: #4a5568;
    font-size: 14px;
}

.task-hierarchy-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.task-hierarchy-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    line-height: 1.6;
    margin-bottom: 4px;
}

.task-hierarchy-item-clickable {
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.task-hierarchy-item-clickable:hover {
    background: rgba(42, 74, 122, 0.1);
    transform: translateX(4px);
}

.task-hierarchy-label {
    color: #718096;
    font-weight: 500;
    min-width: 50px;
}

.task-hierarchy-value {
    color: #4a5568;
    font-weight: 500;
}

.task-conditions-summary {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.task-summary-text {
    color: #4a5568;
    font-size: 13px;
}

.task-pending-review {
    color: #ffa87a;
    font-size: 12px;
    font-weight: 500;
}

.task-pending-review.clickable {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.3s ease;
}

.task-pending-review.clickable:hover {
    color: #ff8a5a;
    text-decoration: underline;
}

.loading-state,
.no-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.loading-state p,
.no-data p {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    margin-top: 16px;
}

/* 響應式設計 */
@media (max-width: 1200px) {
    .content-grid {
        grid-template-columns: 300px 1fr;
    }
}

@media (max-width: 768px) {
    .content-grid {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr;
    }

    .menu-panel {
        height: auto;
        max-height: 300px;
    }

    .menu-list {
        flex-direction: row;
        flex-wrap: wrap;
    }

    .menu-item {
        flex: 1;
        min-width: 120px;
        justify-content: center;
    }

    .menu-item:hover,
    .menu-item.active {
        transform: translateY(-2px);
    }
}

/* 等級紀錄樣式 */
.level-change {
    color: #2a2a3a;
    font-weight: 600;
    font-family: 'Courier New', monospace;
}
</style>

<!-- 組織圖 Modal 樣式不加 scoped，確保掛載到 body 時仍生效 -->
<style>
.orgchart-modal .ant-modal-content {
    background: rgba(255, 255, 255, 0.98);
    border: 2px solid #2a4a7a;
    box-shadow: 0 0 40px rgba(42, 74, 122, 0.3);
}

.orgchart-modal .ant-modal-body {
    background: rgba(255, 255, 255, 0.98);
    padding: 24px;
}

.orgchart-modal .org-chart-wrapper {
    min-height: 72vh;
    background: #ffffff;
}

.orgchart-modal .tree-section {
    background: #ffffff;
    border: 2px solid #2a4a7a;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3);
}

.orgchart-modal .tree-section-title {
    color: #2a4a7a;
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 16px 0;
    text-shadow: 0 0 15px rgba(42, 74, 122, 0.4);
    font-family: 'Courier New', monospace;
    letter-spacing: 1px;
}

.orgchart-modal .tree-container {
    min-height: 200px;
}
</style>
